import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import SignoutHeader from "../subcomponents/SignoutHeader";
import { Colors } from "../Colors";
import axios from "axios";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../store";
import {
  Chip,
  CircularProgress,
  DialogContentText,
  IconButton,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Checkbox,
  ListItemText,
} from "@mui/material";
import {
  AppointmentCategory,
  AppointmentType,
  AuthorizationSource,
  CompleteTemplatePlanOfCare,
  CompleteTemplatePlanOfCareWeek,
  TemplatePlanOfCareAppointment,
  CompleteActivePlanOfCare,
  CompleteActivePlanOfCarePeriod,
  ActivePlanOfCareAppointment,
  WebptPatientAppointment,
  EHR,
  WebptPlanOfCareMetadata,
} from "../types";
import { v4 as uuidv4 } from "uuid";
import { DateTime } from "luxon";
import { add, create, last, set } from "lodash";
import { Link, useNavigate, useParams } from "react-router-dom";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RemoveIcon from "@mui/icons-material/Remove";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import AddIcon from "@mui/icons-material/Add";
import { getWaitlistPatient } from "../slices/PatientSlice";
import { fetchProviders, fetchProviderGroups } from "../slices/ProviderSlice";
import { fetchWebptProviders } from "../slices/WebptIntegrationSlice";
import { DatePicker } from "@mui/x-date-pickers";
import { InfoTooltip } from "../subcomponents/InfoTooltip";
import { useSnackbar } from "../providers/SnackbarProvider";
import { fetchInsurances } from "../slices/InsurancesSlice";
import { api, getNearestSaturday, getNearestSunday, getNextSaturday, getPreviousSunday, overrideDate } from "../utils/utils";
import { filterAppointmentTypesForPicker } from "../utils/appointmentTypes";
import LoadingWithMessage from "../subcomponents/LoadingWithMessage";
import { SecondaryButton } from "../subcomponents/CustomButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SettingsIcon from "@mui/icons-material/Settings";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import { NumberTextField } from "../components/text-fields/NumberTextField";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

const matchAppointmentTypeForCaseStudy = (
  appointmentTypes: AppointmentType[],
  caseStudyName: string | undefined,
  patientInsuranceId: string | undefined,
  category?: AppointmentCategory,
): AppointmentType | undefined => {
  const filtered = category ? appointmentTypes.filter((type) => type.category === category) : appointmentTypes;
  let match: AppointmentType | undefined = undefined;
  if (patientInsuranceId) {
    match = filtered.find((type) => type.insuranceIds?.includes(patientInsuranceId)) ?? match;
  }
  if (caseStudyName) {
    const lowerName = caseStudyName.toLowerCase();
    const keywordMatch = filtered.find((type) => {
      if (!type.keyword) return false;
      const keywords = Array.isArray(type.keyword) ? type.keyword : [type.keyword];
      return keywords.some((k) => k && lowerName.includes(k.toLowerCase()));
    });
    match = keywordMatch ?? match;
  }
  return match;
};

export const extendPlanOfCareToAuthorization = (
  poc: CompleteActivePlanOfCare,
  appointmentData: WebptPatientAppointment[],
  appointmentTypes: AppointmentType[],
  authorizationData: WebptPlanOfCareMetadata,
): CompleteActivePlanOfCare => {
  const frequency = (!poc.ignoreDocumentation && authorizationData.numPerWeek) || poc.frequency || 0;
  if (frequency <= 0) {
    return poc;
  }
  let newPOC = { ...poc };

  let numRemaining = authorizationData.totalAuthorizedVisits - authorizationData.usedAuthorizedVisits;

  for (const week of newPOC.activePlanOfCarePeriods) {
    for (const appointment of week.activePlanOfCareAppointments) {
      if (appointment.status === "missed" || appointment.status === "occurred") {
        continue;
      }

      if (numRemaining > 0) {
        numRemaining--;
      }
    }
  }

  let authorizationExpiration = authorizationData.expirationDate ? DateTime.fromFormat(authorizationData.expirationDate, "yyyy-MM-dd").endOf("day") : null;
  while (numRemaining > 0) {
    const lastWeek = newPOC.activePlanOfCarePeriods[newPOC.activePlanOfCarePeriods.length - 1];
    const lastWeekEnd = DateTime.fromISO(lastWeek.periodEnd || "");
    const lastWeekStart = DateTime.fromISO(lastWeek.periodStart || "");

    // don't extend past authorization expiration date
    if (authorizationExpiration && authorizationExpiration.isValid && lastWeekEnd >= authorizationExpiration) {
      break;
    }

    const newWeekId = uuidv4();
    const periodStart = lastWeekStart.plus({ weeks: 1 }).toISO() || "";
    const periodEnd = lastWeekEnd.plus({ weeks: 1 }).toISO() || "";
    const startDate = getNearestSunday(periodStart) || "";
    const endDate = getNearestSaturday(periodEnd) || "";
    const newWeek: CompleteActivePlanOfCarePeriod = {
      superOrganizationId: newPOC.superOrganizationId,
      organizationId: newPOC.organizationId,

      activePlanOfCareId: newPOC.activePlanOfCareId,
      activePlanOfCarePeriodId: newWeekId,

      periodStart: periodStart,
      periodEnd: periodEnd,

      startDate: startDate,
      endDate: endDate,

      activePlanOfCareAppointments: [],
      activePlanOfCareAppointmentIds: [],

      patientId: newPOC.patientId,
    };

    for (let i = 0; i < frequency; i++) {
      //if (numRemaining <= 0) {
      //  break;
      //}

      const appointmentType = newPOC.defaultAppointmentTypeId
        ? appointmentTypes.find((type) => type.appointmentTypeId === newPOC.defaultAppointmentTypeId)
        : appointmentTypes.find((type) => type.webptShortCode === "DN"); // default to Follow-Up
      if (!appointmentType) continue;
      const newAppointmentId = uuidv4();
      const newAppointment: ActivePlanOfCareAppointment = {
        superOrganizationId: newPOC.superOrganizationId,
        organizationId: newPOC.organizationId,
        activePlanOfCareId: newPOC.activePlanOfCareId,
        activePlanOfCarePeriodId: newWeekId,
        activePlanOfCareAppointmentId: newAppointmentId,
        appointmentTypeId: appointmentType.appointmentTypeId,
        appointmentDate: undefined,
        status: "unscheduled",
        patientId: newPOC.patientId,
        notes: "Created by auto-extending Plan of Care",
      };
      newWeek.activePlanOfCareAppointments.push(newAppointment);
      newWeek.activePlanOfCareAppointmentIds.push(newAppointmentId);
      numRemaining--;
    }

    newPOC.activePlanOfCarePeriods.push(newWeek);
    newPOC.activePlanOfCarePeriodIds.push(newWeekId);
  }

  return newPOC;
};

export const adjustPlanOfCareFrequency = (poc: CompleteActivePlanOfCare, appointmentTypes: AppointmentType[], frequency: number, startDate: string): CompleteActivePlanOfCare => {
  let newPOC = { ...poc };

  const followUp = appointmentTypes.find((type) => type.webptShortCode === "DN"); // default to Follow-Up

  for (const week of poc.activePlanOfCarePeriods) {
    if (week.periodStart > startDate) {
      if (week.activePlanOfCareAppointments.length == frequency) {
        continue;
      } else if (week.activePlanOfCareAppointments.length < frequency) {
        // add appointments to meet the frequency
        const appointmentsToAdd = frequency - week.activePlanOfCareAppointments.length;
        for (let i = 0; i < appointmentsToAdd; i++) {
          const appointment: ActivePlanOfCareAppointment = {
            activePlanOfCareAppointmentId: uuidv4(),
            activePlanOfCarePeriodId: week.activePlanOfCarePeriodId,
            activePlanOfCareId: poc.activePlanOfCareId,

            superOrganizationId: poc.superOrganizationId,
            organizationId: poc.organizationId,
            patientId: poc.patientId,
            appointmentTypeId: poc.defaultAppointmentTypeId || followUp?.appointmentTypeId || "",
            status: "unscheduled",
          };
          week.activePlanOfCareAppointments.push(appointment);
          week.activePlanOfCareAppointmentIds.push(appointment.activePlanOfCareAppointmentId);
        }
      } else if (week.activePlanOfCareAppointments.length > frequency) {
        const truncatedAppointments = week.activePlanOfCareAppointments.slice(0, frequency);
        week.activePlanOfCareAppointments = truncatedAppointments;
        week.activePlanOfCareAppointmentIds = truncatedAppointments.map((appointment) => appointment.activePlanOfCareAppointmentId);
      }
    }
  }
  return newPOC;
};

export const truncatePlanOfCareToAuthorization = (poc: CompleteActivePlanOfCare, authorizationData: WebptPlanOfCareMetadata): CompleteActivePlanOfCare => {
  if (poc.preserve || poc.ignoreDocumentation) {
    return poc;
  }

  const frequency = authorizationData.numPerWeek || 0;

  if (frequency <= 0 || !authorizationData.startDate) {
    return poc;
  }

  console.log("Truncating Plan of Care to Authorization with data", authorizationData);
  let newPOC = { ...poc };

  for (const week of poc.activePlanOfCarePeriods) {
    if (week.preserve) continue; // skip weeks marked to preserve
    if (week.periodStart > authorizationData.startDate) {
      // truncate the week to only have frequency num of appointments
      const truncatedAppointments = week.activePlanOfCareAppointments.slice(0, frequency);
      week.activePlanOfCareAppointments = truncatedAppointments;
      week.activePlanOfCareAppointmentIds = truncatedAppointments.map((appointment) => appointment.activePlanOfCareAppointmentId);
    }
  }
  return newPOC;
};

export const truncatePeriodToAuthorization = (week: CompleteActivePlanOfCarePeriod, authorizationData: WebptPlanOfCareMetadata): CompleteActivePlanOfCarePeriod => {
  if (week.preserve) return week;
  const frequency = authorizationData.numPerWeek || 0;
  if (frequency <= 0 || !authorizationData.startDate) return week;
  if (week.periodStart <= authorizationData.startDate) return week;
  const truncatedAppointments = week.activePlanOfCareAppointments.slice(0, frequency);
  return {
    ...week,
    activePlanOfCareAppointments: truncatedAppointments,
    activePlanOfCareAppointmentIds: truncatedAppointments.map((a) => a.activePlanOfCareAppointmentId),
  };
};

export const extendPlanOfCare = (poc: CompleteActivePlanOfCare, appointmentData: WebptPatientAppointment[], appointmentTypes: AppointmentType[], endDate: string): CompleteActivePlanOfCare => {
  let newPOC = { ...poc };
  while (DateTime.fromISO(newPOC.activePlanOfCarePeriods[newPOC.activePlanOfCarePeriods.length - 1].periodEnd || "") < DateTime.fromISO(endDate)) {
    const frequency = poc.frequency || 0;
    if (frequency <= 0) {
      break;
    }

    const lastWeek = newPOC.activePlanOfCarePeriods[newPOC.activePlanOfCarePeriods.length - 1];
    const lastWeekStart = DateTime.fromISO(lastWeek.periodStart || "");
    const lastWeekEnd = DateTime.fromISO(lastWeek.periodEnd || "");
    const startDate = getNearestSunday(lastWeekStart.plus({ weeks: 1 }).toISO() || "");
    const endDate = getNearestSaturday(lastWeekEnd.plus({ weeks: 1 }).toISO() || "");

    const newWeekId = uuidv4();
    const newWeek: CompleteActivePlanOfCarePeriod = {
      superOrganizationId: newPOC.superOrganizationId,
      organizationId: newPOC.organizationId,

      activePlanOfCareId: newPOC.activePlanOfCareId,
      activePlanOfCarePeriodId: newWeekId,

      periodStart: lastWeekStart.plus({ weeks: 1 }).toISO() || "",
      periodEnd: lastWeekEnd.plus({ weeks: 1 }).toISO() || "",

      startDate: startDate,
      endDate: endDate,

      activePlanOfCareAppointments: [],
      activePlanOfCareAppointmentIds: [],

      patientId: newPOC.patientId,
    };

    // add two appointments per week by default
    for (let i = 0; i < frequency; i++) {
      const appointmentType = newPOC.defaultAppointmentTypeId
        ? appointmentTypes.find((type) => type.appointmentTypeId === newPOC.defaultAppointmentTypeId)
        : appointmentTypes.find((type) => type.webptShortCode === "DN"); // default to Follow-Up
      if (!appointmentType) continue;
      const newAppointmentId = uuidv4();
      const newAppointment: ActivePlanOfCareAppointment = {
        superOrganizationId: newPOC.superOrganizationId,
        organizationId: newPOC.organizationId,
        activePlanOfCareId: newPOC.activePlanOfCareId,
        activePlanOfCarePeriodId: newWeekId,
        activePlanOfCareAppointmentId: newAppointmentId,
        appointmentTypeId: appointmentType.appointmentTypeId,
        appointmentDate: undefined,
        status: "unscheduled",
        patientId: newPOC.patientId,
        notes: "Created by auto-extending Plan of Care",
      };
      newWeek.activePlanOfCareAppointments.push(newAppointment);
      newWeek.activePlanOfCareAppointmentIds.push(newAppointmentId);
    }

    newPOC.activePlanOfCarePeriods.push(newWeek);
    newPOC.activePlanOfCarePeriodIds.push(newWeekId);
  }

  return newPOC;
};

export const addAuthorizationToPlanOfCare = (poc: CompleteActivePlanOfCare, planOfCareMetadata: WebptPlanOfCareMetadata) => {
  poc.authorizationTotal = planOfCareMetadata.totalAuthorizedVisits;
  poc.authorizationUsed = planOfCareMetadata.usedAuthorizedVisits;
  poc.authorizationExpires = DateTime.fromFormat(planOfCareMetadata.expirationDate, "yyyy-MM-dd").toISO() || undefined; // TODO (Hizami): figure out why I need to cast this to undefined
  poc.frequency = (!poc.ignoreDocumentation && planOfCareMetadata.numPerWeek) || poc.frequency || 0;
  return poc;
};

const populatePlanOfCare = (poc: CompleteActivePlanOfCare, appointmentData: WebptPatientAppointment[], appointmentTypes: AppointmentType[]) => {
  const newPlanOfCare: CompleteActivePlanOfCare = { ...poc };
  const lastAppointment = appointmentData[appointmentData.length - 1];
  const caseStudyId = poc.caseStudyId || lastAppointment?.CaseID || undefined;
  newPlanOfCare.caseStudyId = caseStudyId;

  let activeAppointments = appointmentData.filter((appt) => {
    const matchesCaseStudyId = appt.CaseID === caseStudyId;
    const isActive = appt.apptStatus !== "Canc" && appt.apptStatus !== "NoSh";
    return matchesCaseStudyId && isActive;
  });

  let totalAppointments = 0;
  let totalAppointmentsInPast = 0;
  let appointmentsOccurred = 0;
  let appointmentsMissed = 0;
  let appointmentsScheduled = 0;
  let appointmentsUnscheduled = 0;
  let appointmentsUnauthorized = 0;
  let totalExtraAppointments = 0;

  newPlanOfCare?.activePlanOfCarePeriods.forEach((week) => {
    if (!week.startDate) {
      week.startDate = getNearestSunday(week.periodStart);
    }
    if (!week.endDate) {
      week.endDate = getNearestSaturday(week.periodEnd);
    }
    week.periodStart = DateTime.fromISO(week.startDate).startOf("day").toISO() || "";
    week.periodEnd = DateTime.fromISO(week.endDate).endOf("day").toISO() || "";

    const appointments = activeAppointments.filter((appt) => {
      const apptStart = appt.startString;
      return apptStart >= week.periodStart && apptStart <= week.periodEnd;
    });

    let extraAppointments: any[] = [];

    const weekIsInPast = DateTime.now() > DateTime.fromISO(week.periodEnd);
    const weekIsUnauthorized = DateTime.fromISO(week.periodStart) >= DateTime.fromISO(poc.authorizationExpires || "");

    week.activePlanOfCareAppointments.forEach((appointment) => {
      if (weekIsInPast) {
        appointment.status = "missed";
      } else if (weekIsUnauthorized) {
        appointment.status = poc.ignoreAuthorization ? "unscheduled" : "unauthorized";
      } else {
        appointment.status = "unscheduled";
      }
      appointment.appointmentDate = undefined;
    });

    let i = 0;
    for (const appointment of appointments) {
      let old_i = i;
      let match = undefined;
      let bypassType = false; // set to True if we are using a CT appointment as a Follow-Up
      let treatedAsInitialExam = false; // set to True if we are using a CT appointment as an Initial Exam
      while (i < week.activePlanOfCareAppointments.length && !match) {
        const appt = week.activePlanOfCareAppointments[i];
        const apptType = appointmentTypes.find((type) => type.appointmentTypeId === appt.appointmentTypeId);
        const webptShortCode = apptType?.webptShortCode || "";
        if (webptShortCode === appointment.AptType) {
          match = i;
          i++;
          break;
        }
        if (webptShortCode === "CT" && appointment.AptType === "IE") {
          // special case: allow a custom type (CT) to match a "Initial Examination" (IE) appointment in the POC
          match = i;
          bypassType = true;
          treatedAsInitialExam = true;
          i++;
          break;
        }
        if ((webptShortCode === "DN" && appointment.AptType === "CT") || (webptShortCode === "CT" && appointment.AptType === "DN")) {
          // special case: allow a custom type (CT) to match a "Daily Note" (DN) appointment in the POC
          match = i;
          bypassType = true;
          i++;
          break;
        }
        i++;
      }

      if (match !== undefined) {
        const appt = week.activePlanOfCareAppointments[match];
        const status = DateTime.now() < DateTime.fromISO(appointment.startString) ? "scheduled" : "occurred";
        appt.appointmentDate = appointment.startString;
        appt.status = status;
        if (bypassType) {
          appt.notes = treatedAsInitialExam
            ? `Custom-Type appointment treated as an Initial Exam for Plan of Care calculations.`
            : `Custom-Type appointment treated as a Follow-Up for Plan of Care calculations.`;
        }
      } else {
        extraAppointments.push(appointment);
        i = old_i;
      }
    }
    week.extraAppointments = extraAppointments;
    totalExtraAppointments += extraAppointments.length;
  });

  const numAuthorized = (poc.authorizationTotal || 0) - (poc.authorizationUsed || 0);
  let numRemaining = numAuthorized;

  // first, scheduled appointments consume authorization
  newPlanOfCare?.activePlanOfCarePeriods.forEach((week) => {
    week.activePlanOfCareAppointments.forEach((appointment) => {
      if (appointment.status === "scheduled") {
        if (numRemaining > 0) {
          numRemaining--;
        }
      }
    });
  });

  // then unscheduled appointments consume authorization
  newPlanOfCare?.activePlanOfCarePeriods.forEach((week) => {
    week.activePlanOfCareAppointments.forEach((appointment) => {
      if (appointment.status === "unscheduled") {
        if (numRemaining > 0) {
          numRemaining--;
        } else {
          appointment.status = poc.ignoreAuthorization ? "unscheduled" : "unauthorized";
        }
      }
    });
  });

  // count totals after setting statuses
  newPlanOfCare?.activePlanOfCarePeriods.forEach((week) => {
    const weekIsInPast = DateTime.now() > DateTime.fromISO(week.periodEnd);

    week.activePlanOfCareAppointments.forEach((appointment) => {
      totalAppointments++;
      if (weekIsInPast) {
        totalAppointmentsInPast++;
      }

      if (appointment.status === "occurred") {
        appointmentsOccurred++;
      } else if (appointment.status === "scheduled") {
        appointmentsScheduled++;
      } else if (appointment.status === "missed") {
        appointmentsMissed++;
      } else if (appointment.status === "unscheduled") {
        appointmentsUnscheduled++;
      } else if (appointment.status === "unauthorized") {
        appointmentsUnauthorized++;
      }
    });
  });

  const startDate = newPlanOfCare.activePlanOfCarePeriods[0].periodStart;
  const endDate = newPlanOfCare.activePlanOfCarePeriods[newPlanOfCare.activePlanOfCarePeriods.length - 1].periodEnd;
  newPlanOfCare.startDate = startDate;
  newPlanOfCare.endDate = endDate;

  const otherExtraAppointments = activeAppointments.filter((appt) => {
    const apptStart = appt.startString;
    return (apptStart < startDate || apptStart > endDate) && appt.CaseID === caseStudyId;
  });
  totalExtraAppointments += otherExtraAppointments.length;
  newPlanOfCare.extraAppointments = otherExtraAppointments;

  newPlanOfCare.totalAppointments = totalAppointments;
  newPlanOfCare.totalAppointmentsInPast = totalAppointmentsInPast;
  newPlanOfCare.appointmentsOccurred = appointmentsOccurred;
  newPlanOfCare.appointmentsMissed = appointmentsMissed;
  newPlanOfCare.appointmentsScheduled = appointmentsScheduled;
  newPlanOfCare.appointmentsUnscheduled = appointmentsUnscheduled;
  newPlanOfCare.appointmentsUnauthorized = appointmentsUnauthorized;
  newPlanOfCare.totalExtraAppointments = totalExtraAppointments;

  return newPlanOfCare;
};

const createPlanOfCareWeek = (planOfCare: CompleteActivePlanOfCare, appointmentData: WebptPatientAppointment[], appointmentTypes: AppointmentType[]): CompleteActivePlanOfCare => {
  const lastWeek = planOfCare.activePlanOfCarePeriods[planOfCare.activePlanOfCarePeriods.length - 1];
  const lastWeekStart = DateTime.fromISO(lastWeek.periodStart || "");
  const lastWeekEnd = DateTime.fromISO(lastWeek.periodEnd || "");
  const periodStart = lastWeekStart.plus({ weeks: 1 }).toISO() || "";
  const periodEnd = lastWeekEnd.plus({ weeks: 1 }).toISO() || "";
  const startDate = getNearestSunday(periodStart) || "";
  const endDate = getNearestSaturday(periodEnd) || "";

  const newWeekId = uuidv4();

  const followUp = appointmentTypes.find((type) => type.webptShortCode === "DN"); // default to Follow-Up

  const activePlanOfCareAppointments: ActivePlanOfCareAppointment[] = [];
  for (let i = 0; i < (planOfCare.frequency || 0); i++) {
    const appointment: ActivePlanOfCareAppointment = {
      activePlanOfCareAppointmentId: uuidv4(),
      activePlanOfCarePeriodId: newWeekId,
      activePlanOfCareId: planOfCare.activePlanOfCareId,

      superOrganizationId: planOfCare.superOrganizationId,
      organizationId: planOfCare.organizationId,
      patientId: planOfCare.patientId,

      appointmentTypeId: planOfCare.defaultAppointmentTypeId || followUp?.appointmentTypeId || "",
      status: "unscheduled",
    };
    activePlanOfCareAppointments.push(appointment);
  }

  const newWeek: CompleteActivePlanOfCarePeriod = {
    superOrganizationId: planOfCare.superOrganizationId,
    organizationId: planOfCare.organizationId,

    activePlanOfCareId: planOfCare.activePlanOfCareId,
    activePlanOfCarePeriodId: newWeekId,

    periodStart: periodStart,
    periodEnd: periodEnd,

    startDate: startDate,
    endDate: endDate,

    activePlanOfCareAppointments: activePlanOfCareAppointments,
    activePlanOfCareAppointmentIds: activePlanOfCareAppointments.map((a) => a.activePlanOfCareAppointmentId),

    patientId: planOfCare.patientId,
  };

  return populatePlanOfCare(
    {
      ...planOfCare,
      activePlanOfCarePeriods: [...planOfCare.activePlanOfCarePeriods, newWeek],
      activePlanOfCarePeriodIds: [...planOfCare.activePlanOfCarePeriodIds, newWeekId],
    },
    appointmentData,
    appointmentTypes,
  );
};

const prependPlanOfCareWeek = (planOfCare: CompleteActivePlanOfCare, appointmentData: WebptPatientAppointment[], appointmentTypes: AppointmentType[]): CompleteActivePlanOfCare => {
  const firstWeek = planOfCare.activePlanOfCarePeriods[0];
  const firstWeekStart = DateTime.fromISO(firstWeek.periodStart);
  const firstWeekEnd = DateTime.fromISO(firstWeek.periodEnd);
  const periodStart = firstWeekStart.minus({ weeks: 1 }).toISO() || "";
  const periodEnd = firstWeekEnd.minus({ weeks: 1 }).toISO() || "";
  const startDate = getNearestSunday(periodStart) || "";
  const endDate = getNearestSaturday(periodEnd) || "";

  const newWeekId = uuidv4();
  const newWeek: CompleteActivePlanOfCarePeriod = {
    superOrganizationId: planOfCare.superOrganizationId,
    organizationId: planOfCare.organizationId,

    activePlanOfCareId: planOfCare.activePlanOfCareId,
    activePlanOfCarePeriodId: newWeekId,

    periodStart: periodStart,
    periodEnd: periodEnd,

    startDate: startDate,
    endDate: endDate,

    activePlanOfCareAppointments: [],
    activePlanOfCareAppointmentIds: [],

    patientId: planOfCare.patientId,
  };

  return populatePlanOfCare(
    {
      ...planOfCare,
      activePlanOfCarePeriods: [newWeek, ...planOfCare.activePlanOfCarePeriods],
      activePlanOfCarePeriodIds: [newWeekId, ...planOfCare.activePlanOfCarePeriodIds],
    },
    appointmentData,
    appointmentTypes,
  );
};

const updatePlanOfCareWeek = (
  planOfCare: CompleteActivePlanOfCare,
  week: CompleteActivePlanOfCarePeriod,
  appointmentData: WebptPatientAppointment[],
  appointmentTypes: AppointmentType[],
): CompleteActivePlanOfCare => {
  const updatedWeeks = planOfCare.activePlanOfCarePeriods.map((w) => (w.activePlanOfCarePeriodId === week.activePlanOfCarePeriodId ? week : w));
  return populatePlanOfCare({ ...planOfCare, activePlanOfCarePeriods: updatedWeeks }, appointmentData, appointmentTypes);
};

const deletePlanOfCareWeek = (planOfCare: CompleteActivePlanOfCare, weekId: string, appointmentData: WebptPatientAppointment[], appointmentTypes: AppointmentType[]): CompleteActivePlanOfCare => {
  const updatedWeeks = planOfCare.activePlanOfCarePeriods.filter((w) => w.activePlanOfCarePeriodId !== weekId);
  const updatedWeekIds = updatedWeeks.map((w) => w.activePlanOfCarePeriodId);
  return populatePlanOfCare({ ...planOfCare, activePlanOfCarePeriods: updatedWeeks, activePlanOfCarePeriodIds: updatedWeekIds }, appointmentData, appointmentTypes);
};

const convertTemplatePlanOfCareToActive = (
  patientId: string,
  startDate: string,
  templatePlanOfCare: CompleteTemplatePlanOfCare,
  appointmentData: WebptPatientAppointment[],
  appointmentTypes: AppointmentType[],
  caseStudy: { id: number; name: string; isActive: boolean },
): CompleteActivePlanOfCare => {
  const activePlanOfCareId = uuidv4();
  const lastWeek = templatePlanOfCare.templatePlanOfCareWeeks[templatePlanOfCare.templatePlanOfCareWeeks.length - 1];
  const frequency = lastWeek?.templatePlanOfCareAppointments.length || 0;
  const activePlanOfCare: CompleteActivePlanOfCare = {
    superOrganizationId: templatePlanOfCare.superOrganizationId,
    organizationId: templatePlanOfCare.organizationId,

    activePlanOfCareId: activePlanOfCareId,
    name: templatePlanOfCare.name,
    numAppointments: templatePlanOfCare.numAppointments,
    gap: templatePlanOfCare.gap,
    frequency: frequency,

    activePlanOfCarePeriods: [],
    activePlanOfCarePeriodIds: [],

    patientId: patientId,
    caseStudyId: caseStudy.id,

    discharged: !caseStudy.isActive,
    caseStudyName: caseStudy.name,
  };

  // assume startDate is a Sunday, each period should span a week
  // set period start to the start of sunday
  const startDateObj = DateTime.fromISO(startDate);
  let startOfPeriod = DateTime.fromISO(getPreviousSunday(startDateObj.toISO() || ""));
  let endOfPeriod = DateTime.fromISO(getNextSaturday(startDateObj.toISO() || ""));

  for (const week of templatePlanOfCare.templatePlanOfCareWeeks) {
    const startDate = getNearestSunday(startOfPeriod.toISO() || "");
    const endDate = getNearestSaturday(endOfPeriod.toISO() || "");
    const activePlanOfCarePeriodId = uuidv4();
    const activePlanOfCarePeriod: CompleteActivePlanOfCarePeriod = {
      superOrganizationId: templatePlanOfCare.superOrganizationId,
      organizationId: templatePlanOfCare.organizationId,

      activePlanOfCareId: activePlanOfCareId,
      activePlanOfCarePeriodId: activePlanOfCarePeriodId,

      periodStart: startOfPeriod.toISO() || "",
      periodEnd: endOfPeriod.toISO() || "",

      startDate: startDate,
      endDate: endDate,

      activePlanOfCareAppointments: [],
      activePlanOfCareAppointmentIds: [],

      patientId: patientId,
    };

    for (const appointment of week.templatePlanOfCareAppointments) {
      const activePlanOfCareAppointmentId = uuidv4();
      const activePlanOfCareAppointment: ActivePlanOfCareAppointment = {
        superOrganizationId: templatePlanOfCare.superOrganizationId,
        organizationId: templatePlanOfCare.organizationId,

        activePlanOfCareId: activePlanOfCareId,
        activePlanOfCarePeriodId: activePlanOfCarePeriodId,
        activePlanOfCareAppointmentId: activePlanOfCareAppointmentId,

        appointmentTypeId: appointment.appointmentTypeId,
        appointmentDate: undefined, // only for appointments that have already been scheduled
        status: "unscheduled",

        patientId: patientId,
      };
      activePlanOfCarePeriod.activePlanOfCareAppointments.push(activePlanOfCareAppointment);
      activePlanOfCarePeriod.activePlanOfCareAppointmentIds.push(activePlanOfCareAppointmentId);
    }

    activePlanOfCare.activePlanOfCarePeriods.push(activePlanOfCarePeriod);
    activePlanOfCare.activePlanOfCarePeriodIds.push(activePlanOfCarePeriodId);

    startOfPeriod = startOfPeriod.plus({ weeks: 1 });
    endOfPeriod = endOfPeriod.plus({ weeks: 1 });
  }
  return populatePlanOfCare(activePlanOfCare, appointmentData, appointmentTypes);
};

interface DropdownInputProps {
  options: { value: string; label: string }[];
  value: string;
  setter: (value: string) => void;
}

const Dropdown: React.FC<DropdownInputProps> = ({ options, value, setter }) => {
  return (
    <Select
      value={value}
      onChange={(e) => setter(e.target.value)}
      displayEmpty
      IconComponent={ExpandMoreIcon}
      sx={{
        width: "fit-content",
        minWidth: "auto",
        color: "#FFF",
        backgroundColor: "transparent",
        ".MuiSelect-select": {
          display: "flex",
          alignItems: "center",
          padding: "2px 0",
          paddingRight: "20px !important",
          color: "#FFF",
          fontWeight: "600",
          fontSize: "14px",
        },
        "& fieldset": {
          border: "none",
        },
        "&:hover .MuiSelect-select": {
          color: Colors.primary,
        },
        ".MuiSelect-icon": {
          color: "rgba(255, 255, 255, 0.4)",
          right: "0px",
          position: "absolute",
          fontSize: "18px",
          transition: "color 0.2s ease",
          pointerEvents: "none",
        },
        "&:hover .MuiSelect-icon": {
          color: Colors.primary,
        },
      }}
    >
      {options.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </Select>
  );
};

type DeleteButtonProps = {
  onDelete: () => void;
};

const DeleteButton: React.FC<DeleteButtonProps> = ({ onDelete }) => {
  const [open, setOpen] = React.useState(false);

  const handleClickOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleConfirmDelete = () => {
    onDelete(); // call the delete function
    setOpen(false); // close dialog after confirming
  };

  return (
    <>
      {/* Delete button */}
      <Button
        variant="contained"
        onClick={handleClickOpen}
        sx={{
          textTransform: "none",
          fontSize: "14px",
          fontWeight: 600,
          backgroundColor: Colors.error,
          color: Colors.white,
          border: "none",
          "&:hover": {
            backgroundColor: Colors.errorDark,
          },
        }}
      >
        Delete Plan
      </Button>

      {/* Confirmation dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            backgroundColor: Colors.bg3,
            borderRadius: "12px",
            border: `1px solid ${Colors.border}`,
          },
        }}
      >
        <DialogTitle sx={{ color: "#FFF", fontWeight: 600 }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "rgba(255, 255, 255, 0.7)" }}>Are you sure you want to delete this plan of care? This action cannot be undone.</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px" }}>
          <Button
            onClick={handleClose}
            sx={{
              textTransform: "none",
              color: "rgba(255, 255, 255, 0.7)",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.05)",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            sx={{
              textTransform: "none",
              backgroundColor: Colors.error,
              "&:hover": {
                backgroundColor: Colors.errorDark,
              },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

type AppointmentCardProps = {
  appointment: ActivePlanOfCareAppointment;
  appointmentNumber: number;
  appointmentTypes: AppointmentType[];
  updatePlanOfCareAppointment: (appointment: ActivePlanOfCareAppointment) => void;
  handleDeleteAppointment: (appointmentId: string) => void;
  editable: boolean;
  isExtra?: boolean;
  isAuthCovered?: boolean;
  isAuthExhausted?: boolean;
};

const AppointmentCard: React.FC<AppointmentCardProps> = ({
  appointment,
  appointmentNumber,
  appointmentTypes,
  updatePlanOfCareAppointment,
  handleDeleteAppointment,
  editable,
  isExtra,
  isAuthCovered,
  isAuthExhausted,
}) => {
  const organization = useSelector((state: RootState) => state.auth.organization);
  const handleAppointmentTypeChange = (value: string) => {
    const updatedAppointment = { ...appointment, appointmentTypeId: value };
    updatePlanOfCareAppointment(updatedAppointment);
  };
  const appointmentType = appointmentTypes.find((type) => type.appointmentTypeId === appointment.appointmentTypeId);
  const appointmentTypeName = appointmentType ? appointmentType.name : "Unknown Appointment Type";
  const appointmentDate = appointment.appointmentDate ? DateTime.fromISO(appointment.appointmentDate).toFormat("MM/dd/yyyy") : "";
  const appointmentNotes = appointment.notes;

  const showAsAuthorized = appointment.status === "unauthorized" && isAuthCovered && !isAuthExhausted && !!organization?.useMultiAuthSource;
  const showAsExhausted = (appointment.status === "unscheduled" || appointment.status === "unauthorized") && !!isAuthExhausted && !!organization?.useMultiAuthSource;

  const statusStrings = {
    occurred: "Completed on " + appointmentDate,
    scheduled: "Scheduled for " + appointmentDate,
    unscheduled: "Not Scheduled",
    missed: "Missed",
    unauthorized: "Unauthorized",
  };

  const backgroundColors = {
    occurred: "#213E42",
    scheduled: "#2A2D4F",
    unscheduled: "#4F4724",
    missed: "#472B39",
    unauthorized: "#666666",
  };

  const fontColors = {
    occurred: "#22B672",
    scheduled: "#2A7FF3",
    unscheduled: "#F5B200",
    missed: "#F2513C",
    unauthorized: "#DDDDDD",
  };

  const statusString = showAsAuthorized ? "Within Authorization" : showAsExhausted ? "Unauthorized" : statusStrings[appointment.status] || "Unknown Status";
  const backgroundColor = isExtra ? "#666666" : showAsAuthorized ? "#4F4724" : showAsExhausted ? backgroundColors.unauthorized : backgroundColors[appointment.status] || Colors.grey0;
  const fontColor = isExtra ? "#DDDDDD" : showAsAuthorized ? "#F5B200" : showAsExhausted ? fontColors.unauthorized : fontColors[appointment.status] || Colors.grey0;

  return (
    <Box
      sx={{
        position: "relative",
        padding: "14px 16px",
        backgroundColor: isExtra ? "rgba(102, 102, 102, 0.2)" : backgroundColor,
        borderRadius: "8px",
        minWidth: "280px",
        border: `1px solid ${Colors.border}`,
        transition: "border-color 0.2s ease",
        "&:hover": {
          borderColor: Colors.border,
        },
      }}
    >
      {editable && (
        <IconButton
          onClick={() => handleDeleteAppointment(appointment.activePlanOfCareAppointmentId)}
          size="small"
          sx={{
            position: "absolute",
            top: "8px",
            right: "8px",
            width: "20px",
            height: "20px",
            padding: 0,
            color: "rgba(255, 255, 255, 0.3)",
            transition: "color 0.2s ease",
            "&:hover": {
              color: "#F2513C",
              backgroundColor: "transparent",
            },
          }}
        >
          <RemoveIcon sx={{ fontSize: "18px" }} />
        </IconButton>
      )}
      <Box display="flex" flexDirection="column" gap={0.5} pr={editable ? 4 : 0}>
        <Typography variant="caption" fontWeight="500" sx={{ color: "rgba(255, 255, 255, 0.5)", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
          {isExtra ? `Extra Visit` : `Visit #${appointmentNumber}`}
        </Typography>
        {editable ? (
          <Dropdown
            options={filterAppointmentTypesForPicker(appointmentTypes, appointment.appointmentTypeId, organization?.organizationId).map((type) => ({
              value: type.appointmentTypeId,
              label: type.name,
            }))}
            value={appointment.appointmentTypeId}
            setter={handleAppointmentTypeChange}
          />
        ) : (
          <Typography variant="body2" fontWeight="600" sx={{ fontSize: "14px" }}>
            {appointmentTypeName}
          </Typography>
        )}
        <Box display="flex" alignItems="center" gap={0.5}>
          <Box
            sx={{
              width: "6px",
              height: "6px",
              borderRadius: "50%",
              backgroundColor: fontColor,
            }}
          />
          <Typography variant="caption" sx={{ color: fontColor, fontSize: "12px", fontWeight: "500" }}>
            {statusString}
          </Typography>
        </Box>
        {appointmentNotes && (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              px: 1,
              py: 0.25,
              backgroundColor: "rgba(255, 255, 255, 0.06)",
              border: "1px solid rgba(255, 255, 255, 0.08)",
              borderRadius: "4px",
              mt: 0.5,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "rgba(255, 255, 255, 0.6)",
                fontSize: "10px",
                fontWeight: "500",
                lineHeight: 1.2,
              }}
            >
              {appointmentNotes}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

type WeekRowProps = {
  week: CompleteActivePlanOfCarePeriod;
  weekNumber: number;
  baseAppointmentNumber: number;
  appointmentTypes: AppointmentType[];
  handleUpdatePlanOfCareWeek: (week: CompleteActivePlanOfCarePeriod) => void;
  handleDeletePlanOfCareWeek: (weekId: string) => void;
  defaultAppointmentTypeId: string;
  editable: boolean;
  showAddPreviousWeek?: boolean;
  handlePrependPlanOfCareWeek?: () => void;
  showAddNextWeek?: boolean;
  handleCreatePlanOfCareWeek?: () => void;
  showRemoveLastWeek?: boolean;
  handleRemoveLastWeek?: () => void;
  authorizationsPerSource?: {
    prescriptions: { start_date: string; end_date: string; allowed_visits: number | null; used_visits?: number | null; future_visits?: number | null }[];
    authorizations: { start_date: string; end_date: string; allowed_visits: number | null; used_visits?: number | null; future_visits?: number | null }[];
    policies: { start_date: string; end_date: string; allowed_visits: number | null; used_visits?: number | null; future_visits?: number | null }[];
  };
};

const WeekRow: React.FC<WeekRowProps> = ({
  week,
  weekNumber,
  baseAppointmentNumber,
  appointmentTypes,
  handleUpdatePlanOfCareWeek,
  handleDeletePlanOfCareWeek,
  defaultAppointmentTypeId,
  editable,
  showAddPreviousWeek,
  handlePrependPlanOfCareWeek,
  showAddNextWeek,
  handleCreatePlanOfCareWeek,
  showRemoveLastWeek,
  handleRemoveLastWeek,
  authorizationsPerSource,
}) => {
  const organization = useSelector((state: RootState) => state.auth.organization);

  const hasUnscheduled = week.activePlanOfCareAppointments.some((a) => a.status === "unscheduled" || a.status === "unauthorized");

  const authCoverageInfo = React.useMemo(() => {
    if (!authorizationsPerSource) return null;
    const periodStart = week.periodStart.slice(0, 10);
    const periodEnd = week.periodEnd.slice(0, 10);
    const sources = [authorizationsPerSource.prescriptions, authorizationsPerSource.authorizations, authorizationsPerSource.policies];

    const isCovered = sources.every((ranges) => ranges.length === 0 || ranges.some((r) => r.start_date <= periodEnd && r.end_date >= periodStart));
    if (!isCovered) return { covered: false, exhausted: false, isPartial: false, bookableStart: null, bookableEnd: null };

    let bookableStart = periodStart;
    let bookableEnd = periodEnd;
    let exhausted = false;

    for (const ranges of sources) {
      if (ranges.length === 0) continue;
      const covering = ranges.filter((r) => r.start_date <= periodEnd && r.end_date >= periodStart);

      const nonExhausted = covering.filter((r) => {
        const consumed = (r.used_visits ?? 0) + (r.future_visits ?? 0);
        return r.allowed_visits === null || consumed < r.allowed_visits;
      });

      if (nonExhausted.length === 0) {
        // Every covering range in this source is used up
        exhausted = true;
        continue;
      }

      // Bookable window for this source = union of non-exhausted ranges clamped to period
      const sourceStart = nonExhausted.reduce((min, r) => r.start_date < min ? r.start_date : min, nonExhausted[0].start_date);
      const sourceEnd   = nonExhausted.reduce((max, r) => r.end_date   > max ? r.end_date   : max, nonExhausted[0].end_date);
      const clampedStart = sourceStart < periodStart ? periodStart : sourceStart;
      const clampedEnd   = sourceEnd   > periodEnd   ? periodEnd   : sourceEnd;

      // Intersect across sources
      if (clampedStart > bookableStart) bookableStart = clampedStart;
      if (clampedEnd   < bookableEnd)   bookableEnd   = clampedEnd;
    }

    const isPartial = bookableStart > periodStart || bookableEnd < periodEnd;
    return { covered: true, exhausted, isPartial, bookableStart: isPartial ? bookableStart : null, bookableEnd: isPartial ? bookableEnd : null };
  }, [authorizationsPerSource, week.periodStart, week.periodEnd]);

  const isAuthCovered = authCoverageInfo?.covered ?? null;
  const handleTogglePreserve = () => {
    handleUpdatePlanOfCareWeek({ ...week, preserve: !week.preserve });
  };

  const updatePlanOfCareAppointment = (appointment: ActivePlanOfCareAppointment) => {
    const updatedAppointments = week.activePlanOfCareAppointments.map((a) => (a.activePlanOfCareAppointmentId === appointment.activePlanOfCareAppointmentId ? appointment : a));
    const updatedAppointmentIds = updatedAppointments.map((a) => a.activePlanOfCareAppointmentId);
    const updatedWeek = { ...week, preserve: true, activePlanOfCareAppointments: updatedAppointments, activePlanOfCareAppointmentIds: updatedAppointmentIds };
    handleUpdatePlanOfCareWeek(updatedWeek);
  };

  const handleDeleteAppointment = (appointmentId: string) => {
    const updatedAppointments = week.activePlanOfCareAppointments.filter((a) => a.activePlanOfCareAppointmentId !== appointmentId);
    const updatedAppointmentIds = updatedAppointments.map((a) => a.activePlanOfCareAppointmentId);
    const updatedWeek = { ...week, preserve: true, activePlanOfCareAppointments: updatedAppointments, activePlanOfCareAppointmentIds: updatedAppointmentIds };
    handleUpdatePlanOfCareWeek(updatedWeek);
  };

  const handleAddAppointment = () => {
    const newAppointmentId = uuidv4();
    const newAppointment: ActivePlanOfCareAppointment = {
      superOrganizationId: week.superOrganizationId,
      organizationId: week.organizationId,

      activePlanOfCareId: week.activePlanOfCareId,
      activePlanOfCarePeriodId: week.activePlanOfCarePeriodId,
      activePlanOfCareAppointmentId: newAppointmentId,

      appointmentTypeId: defaultAppointmentTypeId,
      appointmentDate: undefined, // only for appointments that have already been scheduled
      status: "unscheduled",

      patientId: week.patientId,
    };
    const updatedAppointments = [...week.activePlanOfCareAppointments, newAppointment];
    const updatedAppointmentIds = [...week.activePlanOfCareAppointmentIds, newAppointmentId];
    const updatedWeek = { ...week, preserve: true, activePlanOfCareAppointments: updatedAppointments, activePlanOfCareAppointmentIds: updatedAppointmentIds };
    handleUpdatePlanOfCareWeek(updatedWeek);
  };

  return (
    <Box
      sx={{
        position: "relative",
        borderRadius: "12px",
        backgroundColor: "rgba(255, 255, 255, 0.04)",
        padding: "20px",
        mb: showAddNextWeek ? 4 : 2,
        mt: showAddPreviousWeek || showRemoveLastWeek ? 4 : 0,
        border: `1px solid ${Colors.border}`,
        transition: "border-color 0.2s ease",
        "&:hover": editable
          ? {
              borderColor: Colors.border,
            }
          : {},
      }}
    >
      {/* Add Previous Week Button */}
      {showAddPreviousWeek && handlePrependPlanOfCareWeek && (
        <Tooltip title="Add week to beginning" arrow>
          <IconButton
            onClick={handlePrependPlanOfCareWeek}
            sx={{
              position: "absolute",
              top: "-14px",
              left: "50%",
              transform: "translateX(-50%)",
              width: "28px",
              height: "28px",
              backgroundColor: Colors.bg,
              color: Colors.primary,
              border: `1px solid ${Colors.border}`,
              zIndex: 100,
              "&:hover": {
                backgroundColor: Colors.bg3,
                borderColor: Colors.border,
              },
            }}
          >
            <AddIcon sx={{ fontSize: "16px" }} />
          </IconButton>
        </Tooltip>
      )}

      {/* Remove Last Week Button */}
      {showRemoveLastWeek && (
        <Tooltip title="Remove last week" arrow>
          <IconButton
            onClick={() => (handleRemoveLastWeek ? handleRemoveLastWeek() : handleDeletePlanOfCareWeek(week.activePlanOfCarePeriodId))}
            sx={{
              position: "absolute",
              top: "-14px",
              left: "50%",
              transform: "translateX(-50%)",
              width: "28px",
              height: "28px",
              backgroundColor: Colors.bg,
              color: "#F2513C",
              border: `1px solid ${Colors.border}`,
              zIndex: 100,
              "&:hover": {
                backgroundColor: Colors.bg3,
                borderColor: Colors.border,
              },
            }}
          >
            <RemoveIcon sx={{ fontSize: "16px" }} />
          </IconButton>
        </Tooltip>
      )}
      {/* Week Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box>
            <Typography variant="h6" fontWeight="600" sx={{ mb: 0.5 }}>
              Week {weekNumber}
            </Typography>
            <Typography variant="body2" sx={{ color: Colors.info }}>
              {DateTime.fromISO(week.periodStart).toFormat("MMM d")} - {DateTime.fromISO(week.periodEnd).toFormat("MMM d, yyyy")}
            </Typography>
          </Box>
          {week.preserve && (
            <Tooltip title="This week is protected from auto-adjustments" arrow>
              <LockIcon sx={{ fontSize: "16px", color: Colors.primary, mt: 0.25 }} />
            </Tooltip>
          )}
          {organization?.useMultiAuthSource && hasUnscheduled && authCoverageInfo !== null && (() => {
            const { covered, exhausted, isPartial, bookableStart, bookableEnd } = authCoverageInfo!;
            let label: string;
            let bg: string;
            let fg: string;
            let border: string;
            let tooltipTitle: string | undefined;

            if (!covered) {
              label = "No auth"; bg = "rgba(245, 178, 0, 0.15)"; fg = "#F5B200"; border = "rgba(245, 178, 0, 0.3)";
            } else if (exhausted) {
              label = "Limit reached"; bg = "rgba(242, 81, 60, 0.15)"; fg = "#F2513C"; border = "rgba(242, 81, 60, 0.3)";
            } else if (isPartial && bookableStart && bookableEnd) {
              const fmt = (d: string) => DateTime.fromISO(d).toFormat("MMM d");
              label = "Partial";
              bg = "rgba(34, 182, 114, 0.15)"; fg = "#22B672"; border = "rgba(34, 182, 114, 0.3)";
              tooltipTitle = `Bookable ${fmt(bookableStart)} – ${fmt(bookableEnd)}`;
            } else {
              label = "Bookable"; bg = "rgba(34, 182, 114, 0.15)"; fg = "#22B672"; border = "rgba(34, 182, 114, 0.3)";
            }

            const chip = (
              <Chip size="small" label={label} sx={{ height: 20, fontSize: "11px", fontWeight: 600, backgroundColor: bg, color: fg, border: `1px solid ${border}` }} />
            );
            return tooltipTitle ? <Tooltip title={tooltipTitle} arrow>{chip}</Tooltip> : chip;
          })()}
        </Box>

        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {editable && (
            <Tooltip title={week.preserve ? "Remove protection from auto-adjustments" : "Protect this week from auto-adjustments"} arrow>
              <IconButton
                onClick={handleTogglePreserve}
                size="small"
                sx={{
                  color: week.preserve ? Colors.primary : "rgba(255,255,255,0.3)",
                  "&:hover": { color: Colors.primary },
                }}
              >
                {week.preserve ? <LockIcon sx={{ fontSize: "18px" }} /> : <LockOpenIcon sx={{ fontSize: "18px" }} />}
              </IconButton>
            </Tooltip>
          )}
          {/* Add Visit */}
          {editable && (
            <Button
              onClick={handleAddAppointment}
              variant="outlined"
              size="small"
              startIcon={<AddCircleOutlineIcon />}
              sx={{
                textTransform: "none",
                borderColor: Colors.border,
                "&:hover": {
                  borderColor: Colors.border,
                },
              }}
            >
              Add Visit
            </Button>
          )}
        </Box>
      </Box>

      {/* Appointments Grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(auto-fill, minmax(280px, 1fr))",
          },
          gap: 1.5,
        }}
      >
        {week.activePlanOfCareAppointments.map((appointment, j) => {
          const appointmentNumber = baseAppointmentNumber + j + 1;
          return (
            <AppointmentCard
              key={appointment.activePlanOfCareAppointmentId}
              appointment={appointment}
              appointmentNumber={appointmentNumber}
              appointmentTypes={appointmentTypes}
              updatePlanOfCareAppointment={updatePlanOfCareAppointment}
              handleDeleteAppointment={handleDeleteAppointment}
              editable={editable}
              isAuthCovered={isAuthCovered === true}
              isAuthExhausted={authCoverageInfo?.exhausted === true}
            />
          );
        })}

        {(week.extraAppointments || []).map((appointment) => {
          const activePlanOfCareAppointmentId = uuidv4();
          let appointmentTypeId = appointmentTypes.find((type) => type.webptShortCode === appointment.AptType)?.appointmentTypeId || "";
          if (appointment.AptType === "CT") appointmentTypeId = ""; // we can't tell which custom type this is just by the short code
          const pocAppointment: ActivePlanOfCareAppointment = {
            activePlanOfCareId: week.activePlanOfCareId,
            activePlanOfCarePeriodId: week.activePlanOfCarePeriodId,
            activePlanOfCareAppointmentId: activePlanOfCareAppointmentId,
            superOrganizationId: week.superOrganizationId,
            organizationId: week.organizationId,
            patientId: week.patientId,

            appointmentTypeId: appointmentTypeId,
            appointmentDate: appointment.startString,
            status: "scheduled",
          };

          return (
            <AppointmentCard
              key={appointment.activePlanOfCareAppointmentId}
              appointment={pocAppointment}
              appointmentNumber={0}
              appointmentTypes={appointmentTypes}
              updatePlanOfCareAppointment={updatePlanOfCareAppointment}
              handleDeleteAppointment={handleDeleteAppointment}
              editable={false}
              isExtra={true}
            />
          );
        })}
      </Box>

      {/* Add Next Week Button */}
      {showAddNextWeek && handleCreatePlanOfCareWeek && (
        <Tooltip title="Add week to end" arrow>
          <IconButton
            onClick={handleCreatePlanOfCareWeek}
            sx={{
              position: "absolute",
              bottom: "-14px",
              left: "50%",
              transform: "translateX(-50%)",
              width: "28px",
              height: "28px",
              backgroundColor: Colors.bg,
              color: Colors.primary,
              border: `1px solid ${Colors.border}`,
              zIndex: 100,
              "&:hover": {
                backgroundColor: Colors.bg3,
                borderColor: Colors.border,
              },
            }}
          >
            <AddIcon sx={{ fontSize: "16px" }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

type PlanOfCarePreviewProps = {
  activePlanOfCare: CompleteActivePlanOfCare;
  appointmentTypes: AppointmentType[];
};

type PlanOfCareWeeksWithCollapseProps = {
  activePlanOfCare: CompleteActivePlanOfCare;
  appointmentTypes: AppointmentType[];
  handleUpdatePlanOfCareWeek: (week: CompleteActivePlanOfCarePeriod) => void;
  handleDeletePlanOfCareWeek: (weekId: string) => void;
  defaultAppointmentTypeId: string;
  editable: boolean;
  handlePrependPlanOfCareWeek?: () => void;
  handleCreatePlanOfCareWeek?: () => void;
  handleRemoveLastWeek?: () => void;
  authorizationsPerSource?: {
    prescriptions: { start_date: string; end_date: string; allowed_visits: number | null; used_visits?: number | null; future_visits?: number | null }[];
    authorizations: { start_date: string; end_date: string; allowed_visits: number | null; used_visits?: number | null; future_visits?: number | null }[];
    policies: { start_date: string; end_date: string; allowed_visits: number | null; used_visits?: number | null; future_visits?: number | null }[];
  };
  authorizationSourcePriority?: AuthorizationSource[];
};

const PlanOfCareWeeksWithCollapse: React.FC<PlanOfCareWeeksWithCollapseProps> = ({
  activePlanOfCare,
  appointmentTypes,
  handleUpdatePlanOfCareWeek,
  handleDeletePlanOfCareWeek,
  defaultAppointmentTypeId,
  editable,
  handlePrependPlanOfCareWeek,
  handleCreatePlanOfCareWeek,
  handleRemoveLastWeek,
  authorizationsPerSource,
  authorizationSourcePriority,
}) => {
  const organization = useSelector((state: RootState) => state.auth.organization);
  const [showPastWeeks, setShowPastWeeks] = React.useState(false);

  const now = DateTime.now();
  const pastWeeks = React.useMemo(() => activePlanOfCare.activePlanOfCarePeriods.filter((week) => DateTime.fromISO(week.periodEnd) < now), [activePlanOfCare.activePlanOfCarePeriods, now]);

  const upcomingWeeks = React.useMemo(() => activePlanOfCare.activePlanOfCarePeriods.filter((week) => DateTime.fromISO(week.periodEnd) >= now), [activePlanOfCare.activePlanOfCarePeriods, now]);

  const summary = React.useMemo(() => {
    const occurred = pastWeeks.reduce((sum, week) => sum + week.activePlanOfCareAppointments.filter((a) => a.status === "occurred").length, 0);
    return { occurred };
  }, [pastWeeks]);

  // Pre-calculate all week data to avoid lag
  const pastWeeksData = React.useMemo(
    () =>
      pastWeeks.map((week) => {
        const weekIndex = activePlanOfCare.activePlanOfCarePeriods.indexOf(week);
        const baseAppointmentNumber = activePlanOfCare.activePlanOfCarePeriods.slice(0, weekIndex).reduce((acc, curr) => acc + curr.activePlanOfCareAppointments.length, 0);
        return { week, weekIndex, baseAppointmentNumber };
      }),
    [pastWeeks, activePlanOfCare.activePlanOfCarePeriods],
  );

  const showAuthStrips = organization?.useMultiAuthSource && authorizationsPerSource;

  // Filter out sources that aren't in the priority list before passing to WeekRow,
  // so not-applicable sources don't affect coverage/exhaustion chip logic.
  const filteredAuthorizationsPerSource = React.useMemo(() => {
    if (!authorizationsPerSource) return undefined;
    if (!authorizationSourcePriority || authorizationSourcePriority.length === 0) return authorizationsPerSource;
    return {
      prescriptions: authorizationSourcePriority.includes("prescription") ? authorizationsPerSource.prescriptions : [],
      authorizations: authorizationSourcePriority.includes("authorization") ? authorizationsPerSource.authorizations : [],
      policies: authorizationSourcePriority.includes("policy") ? authorizationsPerSource.policies : [],
    };
  }, [authorizationsPerSource, authorizationSourcePriority]);


  // --- Collapsed mode: date-percentage against upcoming weeks + spacer for the accordion header ---
  const pastWeeksBoxRef = React.useRef<HTMLDivElement>(null);
  const [pastWeeksSectionHeight, setPastWeeksSectionHeight] = React.useState(0);
  React.useEffect(() => {
    const el = pastWeeksBoxRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setPastWeeksSectionHeight(el.offsetHeight));
    ro.observe(el);
    setPastWeeksSectionHeight(el.offsetHeight);
    return () => ro.disconnect();
  }, [pastWeeks.length, showPastWeeks]);

  // Collapsed: strips cover upcoming weeks only (spacer handles the accordion offset)
  const collapsedPlanStart = upcomingWeeks[0]?.periodStart?.slice(0, 10) ?? activePlanOfCare.activePlanOfCarePeriods[0]?.periodStart?.slice(0, 10) ?? "";
  const collapsedPlanEnd = activePlanOfCare.activePlanOfCarePeriods[activePlanOfCare.activePlanOfCarePeriods.length - 1]?.periodEnd?.slice(0, 10) ?? "";
  const collapsedTotalDays = React.useMemo(() => {
    if (!collapsedPlanStart || !collapsedPlanEnd) return 1;
    return DateTime.fromISO(collapsedPlanEnd).diff(DateTime.fromISO(collapsedPlanStart), "days").days + 1;
  }, [collapsedPlanStart, collapsedPlanEnd]);

  const getCollapsedStripPosition = React.useCallback((startDate: string, endDate: string) => {
    const s = startDate < collapsedPlanStart ? collapsedPlanStart : startDate;
    const e = endDate > collapsedPlanEnd ? collapsedPlanEnd : endDate;
    const topDays = DateTime.fromISO(s).diff(DateTime.fromISO(collapsedPlanStart), "days").days;
    const heightDays = DateTime.fromISO(e).diff(DateTime.fromISO(s), "days").days + 1;
    return { top: `${(topDays / collapsedTotalDays) * 100}%`, height: `${(heightDays / collapsedTotalDays) * 100}%` };
  }, [collapsedPlanStart, collapsedPlanEnd, collapsedTotalDays]);

  // Expanded: strips cover the full plan range (past + upcoming)
  const expandedPlanStart = activePlanOfCare.activePlanOfCarePeriods[0]?.periodStart?.slice(0, 10) ?? "";
  const expandedPlanEnd = collapsedPlanEnd;
  const expandedTotalDays = React.useMemo(() => {
    if (!expandedPlanStart || !expandedPlanEnd) return 1;
    return DateTime.fromISO(expandedPlanEnd).diff(DateTime.fromISO(expandedPlanStart), "days").days + 1;
  }, [expandedPlanStart, expandedPlanEnd]);

  const getExpandedStripPosition = React.useCallback((startDate: string, endDate: string) => {
    const s = startDate < expandedPlanStart ? expandedPlanStart : startDate;
    const e = endDate > expandedPlanEnd ? expandedPlanEnd : endDate;
    const topDays = DateTime.fromISO(s).diff(DateTime.fromISO(expandedPlanStart), "days").days;
    const heightDays = DateTime.fromISO(e).diff(DateTime.fromISO(s), "days").days + 1;
    return { top: `${(topDays / expandedTotalDays) * 100}%`, height: `${(heightDays / expandedTotalDays) * 100}%` };
  }, [expandedPlanStart, expandedPlanEnd, expandedTotalDays]);

  const authSources = React.useMemo(() => {
    const hasPriority = !!authorizationSourcePriority && authorizationSourcePriority.length > 0;
    return [
      { key: "prescriptions", authSourceKey: "prescription" as AuthorizationSource, label: "Rx", displayName: "Prescription", color: "#4A90E2", ranges: authorizationsPerSource?.prescriptions ?? [], isNotApplicable: hasPriority && !authorizationSourcePriority!.includes("prescription") },
      { key: "authorizations", authSourceKey: "authorization" as AuthorizationSource, label: "Auth", displayName: "Authorization", color: "#22B672", ranges: authorizationsPerSource?.authorizations ?? [], isNotApplicable: hasPriority && !authorizationSourcePriority!.includes("authorization") },
      { key: "policies", authSourceKey: "policy" as AuthorizationSource, label: "Policy", displayName: "Insurance Policy", color: "#A78BFA", ranges: authorizationsPerSource?.policies ?? [], isNotApplicable: hasPriority && !authorizationSourcePriority!.includes("policy") },
    ];
  }, [authorizationsPerSource, authorizationSourcePriority]);

  return (
    <Box sx={{ display: "flex", gap: 1.5, alignItems: "stretch" }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Past Weeks Accordion */}
        {pastWeeks.length > 0 && (
          <Box ref={pastWeeksBoxRef} sx={{ mb: 2, position: "relative" }}>
            <Box
              onClick={() => setShowPastWeeks(!showPastWeeks)}
              sx={{
                backgroundColor: showPastWeeks ? "rgba(255, 255, 255, 0.04)" : "rgba(255, 255, 255, 0.02)",
                border: `1px solid ${Colors.border}`,
                borderRadius: "8px",
                padding: "12px 16px",
                cursor: "pointer",
                transition: "background-color 0.15s ease",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <ExpandMoreIcon
                  sx={{
                    fontSize: 20,
                    color: showPastWeeks ? "rgba(255, 255, 255, 0.7)" : "rgba(255, 255, 255, 0.4)",
                    transform: showPastWeeks ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "all 0.15s ease",
                  }}
                />
                <Typography
                  variant="body2"
                  fontWeight="600"
                  sx={{
                    color: showPastWeeks ? "#FFF" : "rgba(255, 255, 255, 0.9)",
                    transition: "color 0.15s ease",
                  }}
                >
                  {showPastWeeks ? "Hide" : "Show"} Past Weeks
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.4)", ml: "auto" }}>
                  {summary.occurred} completed
                </Typography>
              </Box>
            </Box>

            {/* Collapsed Past Weeks Content - Instant render with slide animation */}
            {showPastWeeks && (
              <Box
                sx={{
                  mt: 1.5,
                  animation: "slideDown 0.2s ease-out",
                  "@keyframes slideDown": {
                    from: {
                      opacity: 0,
                      transform: "translateY(-8px)",
                    },
                    to: {
                      opacity: 1,
                      transform: "translateY(0)",
                    },
                  },
                }}
              >
                {pastWeeksData.map(({ week, weekIndex, baseAppointmentNumber }) => (
                  <WeekRow
                    key={week.activePlanOfCarePeriodId}
                    week={week}
                    weekNumber={weekIndex + 1}
                    baseAppointmentNumber={baseAppointmentNumber}
                    appointmentTypes={appointmentTypes}
                    handleUpdatePlanOfCareWeek={handleUpdatePlanOfCareWeek}
                    handleDeletePlanOfCareWeek={handleDeletePlanOfCareWeek}
                    defaultAppointmentTypeId={defaultAppointmentTypeId}
                    editable={editable}
                    showAddPreviousWeek={weekIndex === 0 && !!handlePrependPlanOfCareWeek}
                    handlePrependPlanOfCareWeek={handlePrependPlanOfCareWeek}
                    authorizationsPerSource={filteredAuthorizationsPerSource}
                  />
                ))}
              </Box>
            )}
          </Box>
        )}

        {/* Upcoming Weeks */}
        {upcomingWeeks.map((week, idx) => {
          const weekIndex = activePlanOfCare.activePlanOfCarePeriods.indexOf(week);
          const baseAppointmentNumber = activePlanOfCare.activePlanOfCarePeriods.slice(0, weekIndex).reduce((acc, curr) => acc + curr.activePlanOfCareAppointments.length, 0);
          // Only show button on Week 1 (weekIndex === 0)
          const isWeekOne = weekIndex === 0;
          const isLastWeek = idx === upcomingWeeks.length - 1;
          const canRemoveWeek = activePlanOfCare.activePlanOfCarePeriods.length > 1;
          return (
            <WeekRow
              key={week.activePlanOfCarePeriodId}
              week={week}
              weekNumber={weekIndex + 1}
              baseAppointmentNumber={baseAppointmentNumber}
              appointmentTypes={appointmentTypes}
              handleUpdatePlanOfCareWeek={handleUpdatePlanOfCareWeek}
              handleDeletePlanOfCareWeek={handleDeletePlanOfCareWeek}
              defaultAppointmentTypeId={defaultAppointmentTypeId}
              editable={editable}
              showAddPreviousWeek={isWeekOne && !!handlePrependPlanOfCareWeek}
              handlePrependPlanOfCareWeek={handlePrependPlanOfCareWeek}
              showAddNextWeek={isLastWeek && !!handleCreatePlanOfCareWeek}
              handleCreatePlanOfCareWeek={handleCreatePlanOfCareWeek}
              showRemoveLastWeek={isLastWeek && canRemoveWeek && !!handleCreatePlanOfCareWeek}
              handleRemoveLastWeek={handleRemoveLastWeek}
              authorizationsPerSource={filteredAuthorizationsPerSource}
            />
          );
        })}
      </Box>
      {showAuthStrips && (
        <Box sx={{ width: 96, flexShrink: 0, display: "flex", flexDirection: "column" }}>
          {/* Column headers */}
          <Box sx={{ position: "sticky", top: 64, zIndex: 1, backgroundColor: Colors.bg, pb: 0.5 }}>
            <Box sx={{ display: "flex", gap: 0.5 }}>
              {authSources.map(({ key, label, color, isNotApplicable, displayName }) => (
                <Tooltip key={key} title={isNotApplicable ? <Box><Typography variant="caption" display="block" fontWeight={600}>{displayName}</Typography><Typography variant="caption" display="block">Not required for this insurance</Typography></Box> : ""} placement="left" arrow disableHoverListener={!isNotApplicable}>
                  <Typography variant="caption" sx={{ flex: 1, fontSize: "9px", color: isNotApplicable ? "rgba(255,255,255,0.2)" : color, textAlign: "center", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.3px", textDecoration: isNotApplicable ? "line-through" : "none", cursor: isNotApplicable ? "default" : "inherit" }}>
                    {label}
                  </Typography>
                </Tooltip>
              ))}
            </Box>
          </Box>
          {/* Collapsed: spacer + continuous date-percentage strips. Expanded: per-row measured segments. */}
          {!showPastWeeks && <Box sx={{ height: pastWeeksSectionHeight }} />}
          <Box sx={{ flex: 1, position: "relative", display: "flex", gap: 0.5 }}>
            {authSources.map(({ key, color, ranges, displayName, isNotApplicable }) => (
              <Box key={key} sx={{ flex: 1, position: "relative" }}>
                {isNotApplicable ? (
                  <Tooltip title={<Box><Typography variant="caption" display="block" fontWeight={600}>{displayName}</Typography><Typography variant="caption" display="block">Not required for this insurance</Typography></Box>} placement="left" arrow>
                    <Box sx={{ position: "absolute", inset: 0, borderRadius: "4px", border: "1px dashed rgba(255,255,255,0.08)", cursor: "default", backgroundImage: "repeating-linear-gradient(135deg, transparent, transparent 4px, rgba(255,255,255,0.03) 4px, rgba(255,255,255,0.03) 8px)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Typography sx={{ writingMode: "vertical-rl", fontSize: "8px", color: "rgba(255,255,255,0.15)", fontWeight: 600, userSelect: "none", pointerEvents: "none", letterSpacing: "0.5px" }}>N/A</Typography>
                    </Box>
                  </Tooltip>
                ) : ranges.length === 0 ? (
                  <Tooltip
                    title={
                      <Box>
                        <Typography variant="caption" display="block" fontWeight={600}>{displayName}</Typography>
                        <Typography variant="caption" display="block">Left blank</Typography>
                      </Box>
                    }
                    placement="left"
                    arrow
                  >
                    <Box sx={{ position: "absolute", inset: 0, backgroundColor: color, opacity: 0.08, borderRadius: "4px", border: `1px dashed ${color}`, cursor: "default" }} />
                  </Tooltip>
                ) : showPastWeeks ? (
                  // Expanded: continuous date-percentage strips spanning the full plan range
                  ranges
                    .filter((r) => r.start_date <= expandedPlanEnd && r.end_date >= expandedPlanStart)
                    .map((range, i) => {
                      const { top, height } = getExpandedStripPosition(range.start_date, range.end_date);
                      const consumed = (range.used_visits ?? 0) + (range.future_visits ?? 0);
                      const remaining = range.allowed_visits != null ? range.allowed_visits - consumed : null;
                      const isDepleted = remaining !== null && remaining <= 0;
                      return (
                        <Tooltip key={i} title={
                          <Box>
                            <Typography variant="caption" display="block" fontWeight={600}>{displayName}</Typography>
                            <Typography variant="caption" display="block">{DateTime.fromISO(range.start_date).toFormat("MMM d yyyy")} – {DateTime.fromISO(range.end_date).toFormat("MMM d yyyy")}</Typography>
                            <Typography variant="caption" display="block">
                              {range.allowed_visits != null ? `${consumed} of ${range.allowed_visits} used` : `${consumed} used (unlimited)`}
                            </Typography>
                            {remaining != null && <Typography variant="caption" display="block">{remaining} left</Typography>}
                          </Box>
                        } placement="left" arrow>
                          <Box sx={{ position: "absolute", top, height, left: 0, right: 0, borderRadius: "4px", cursor: "default", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: `${color}${isDepleted ? "80" : "40"}`, border: `1px ${isDepleted ? "dashed" : "solid"} ${color}`, ...(isDepleted && { backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(0,0,0,0.25) 3px, rgba(0,0,0,0.25) 6px)" }), transition: "background-color 0.15s ease", "&:hover": { backgroundColor: `${color}${isDepleted ? "40" : "20"}` } }}>
                            {range.allowed_visits != null && (
                              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "1px", userSelect: "none", pointerEvents: "none" }}>
                                <Typography sx={{ fontSize: "10px", color, fontWeight: 700, lineHeight: 1 }}>{consumed}</Typography>
                                <Box sx={{ width: "14px", height: "1px", backgroundColor: color }} />
                                <Typography sx={{ fontSize: "10px", color, fontWeight: 700, lineHeight: 1 }}>{range.allowed_visits}</Typography>
                              </Box>
                            )}
                          </Box>
                        </Tooltip>
                      );
                    })
                ) : (
                  // Collapsed: continuous strips date-percentage against upcoming weeks range
                  ranges
                    .filter((r) => r.start_date <= collapsedPlanEnd && r.end_date >= collapsedPlanStart)
                    .map((range, i) => {
                      const { top, height } = getCollapsedStripPosition(range.start_date, range.end_date);
                      const consumed = (range.used_visits ?? 0) + (range.future_visits ?? 0);
                      const remaining = range.allowed_visits != null ? range.allowed_visits - consumed : null;
                      const isDepleted = remaining !== null && remaining <= 0;
                      return (
                        <Tooltip key={i} title={
                          <Box>
                            <Typography variant="caption" display="block" fontWeight={600}>{displayName}</Typography>
                            <Typography variant="caption" display="block">{DateTime.fromISO(range.start_date).toFormat("MMM d yyyy")} – {DateTime.fromISO(range.end_date).toFormat("MMM d yyyy")}</Typography>
                            <Typography variant="caption" display="block">
                              {range.allowed_visits != null ? `${consumed} of ${range.allowed_visits} used` : `${consumed} used (unlimited)`}
                            </Typography>
                            {remaining != null && <Typography variant="caption" display="block">{remaining} left</Typography>}
                          </Box>
                        } placement="left" arrow>
                          <Box sx={{ position: "absolute", top, height, left: 0, right: 0, borderRadius: "4px", cursor: "default", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: `${color}${isDepleted ? "80" : "40"}`, border: `1px ${isDepleted ? "dashed" : "solid"} ${color}`, ...(isDepleted && { backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(0,0,0,0.25) 3px, rgba(0,0,0,0.25) 6px)" }), transition: "background-color 0.15s ease", "&:hover": { backgroundColor: `${color}${isDepleted ? "40" : "20"}` } }}>
                            {range.allowed_visits != null && (
                              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "1px", userSelect: "none", pointerEvents: "none" }}>
                                <Typography sx={{ fontSize: "10px", color, fontWeight: 700, lineHeight: 1 }}>{consumed}</Typography>
                                <Box sx={{ width: "14px", height: "1px", backgroundColor: color }} />
                                <Typography sx={{ fontSize: "10px", color, fontWeight: 700, lineHeight: 1 }}>{range.allowed_visits}</Typography>
                              </Box>
                            )}
                          </Box>
                        </Tooltip>
                      );
                    })
                )}
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export const PlanOfCareActivePreview: React.FC<PlanOfCarePreviewProps> = ({ activePlanOfCare, appointmentTypes }) => {
  return (
    <Box sx={{ width: "100%" }}>
      {activePlanOfCare.activePlanOfCarePeriods.map((week, i) => {
        const baseAppointmentNumber = activePlanOfCare.activePlanOfCarePeriods.slice(0, i).reduce((acc, curr) => acc + curr.activePlanOfCareAppointments.length, 0);
        return (
          <div key={week.activePlanOfCarePeriodId}>
            <WeekRow
              week={week}
              weekNumber={i + 1}
              baseAppointmentNumber={baseAppointmentNumber}
              appointmentTypes={appointmentTypes}
              handleUpdatePlanOfCareWeek={() => {}}
              handleDeletePlanOfCareWeek={() => {}}
              defaultAppointmentTypeId={""}
              editable={false}
            />
          </div>
        );
      })}
      {activePlanOfCare.extraAppointments &&
        activePlanOfCare.extraAppointments.map((appointment) => {
          const activePlanOfCareAppointmentId = uuidv4();
          let appointmentTypeId = appointmentTypes.find((type) => type.webptShortCode === appointment.AptType)?.appointmentTypeId || "";
          if (appointment.AptType === "CT") appointmentTypeId = ""; // we can't tell which custom type this is just by the short code
          const pocAppointment: ActivePlanOfCareAppointment = {
            activePlanOfCareId: activePlanOfCare.activePlanOfCareId,
            activePlanOfCarePeriodId: "",
            activePlanOfCareAppointmentId: activePlanOfCareAppointmentId,
            superOrganizationId: activePlanOfCare.superOrganizationId,
            organizationId: activePlanOfCare.organizationId,
            patientId: "",

            appointmentTypeId: appointmentTypeId,
            appointmentDate: appointment.startString,
            status: "scheduled",
          };

          return (
            <AppointmentCard
              key={activePlanOfCareAppointmentId}
              appointment={pocAppointment}
              appointmentNumber={0}
              appointmentTypes={appointmentTypes}
              updatePlanOfCareAppointment={() => {}}
              handleDeleteAppointment={() => {}}
              editable={false}
              isExtra={true}
            />
          );
        })}
    </Box>
  );
};

export const PlanOfCareActiveViewPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { showMessage } = useSnackbar();

  const planOfCareId = useParams().planOfCareId || "";
  const patientId = useParams().patientId || "";

  const user = useSelector((state: RootState) => state.auth.user);
  const organization = useSelector((state: RootState) => state.auth.organization);
  const superOrganization = useSelector((state: RootState) => state.auth.superOrganization);
  const organizationId = user?.organizationId || "";
  const patients = useSelector((state: RootState) => state.patients.patientMap);
  const patient = patients[patientId];
  const { insurances } = useSelector((state: RootState) => state.insurances);

  const [appointmentTypes, setAppointmentTypes] = React.useState<AppointmentType[]>([]);
  const [appointmentData, setAppointmentData] = React.useState<WebptPatientAppointment[]>([]);
  const [authorization, setAuthorization] = React.useState<WebptPlanOfCareMetadata | null>(null);
  const [activePlanOfCare, setActivePlanOfCare] = React.useState<CompleteActivePlanOfCare | null>(null);

  const [firstVisit, setFirstVisit] = React.useState<AppointmentType | undefined>(undefined);
  const [mostVisits, setMostVisits] = React.useState<AppointmentType | undefined>(undefined);

  const authorizationSourcePriority = React.useMemo(() => {
    if (!patient?.patient.insuranceId || !insurances.length) return undefined;
    return insurances.find((i) => i.insuranceId === patient.patient.insuranceId)?.authorizationSourcePriority;
  }, [patient?.patient.insuranceId, insurances]);

  const handleSetMostVisits = (type: AppointmentType) => {
    setMostVisits(type);
    if (activePlanOfCare) {
      setActivePlanOfCare({ ...activePlanOfCare, defaultAppointmentTypeId: type.appointmentTypeId });
    }
  };

  const handleSetFrequency = (frequency: number) => {
    if (activePlanOfCare && authorization) {
      setActivePlanOfCare((prev) => {
        if (!prev) return prev;
        const frequencyPlanOfCare = { ...prev, frequency: frequency, ignoreDocumentation: true };
        const adjustedPlanOfCare = adjustPlanOfCareFrequency(frequencyPlanOfCare, appointmentTypes, frequency, DateTime.now().minus({ weeks: 1 }).toISO());
        const populatedPlanOfCare = populatePlanOfCare(adjustedPlanOfCare, appointmentData, appointmentTypes);
        return populatedPlanOfCare;
      });
    }
  };

  const hasWebptIntegration = organization?.integrations.includes(EHR.webpt);
  const ehrId = patient?.[EHR.webpt]?.webptPatientId;

  const [caseStudies, setCaseStudies] = React.useState<{ id: number; name: string; isActive: boolean }[]>([]); // TODO (Hizami): Replace with actual type
  const [caseStudyId, setCaseStudyId] = React.useState<number>(0);
  const [editCaseStudy, setEditCaseStudy] = React.useState<boolean>(false);

  const [loadingAppointmentTypes, setLoadingAppointmentTypes] = React.useState<boolean>(true);
  const [loadingAppointmentData, setLoadingAppointmentData] = React.useState<boolean>(true);
  const [loadingCaseStudies, setLoadingCaseStudies] = React.useState<boolean>(true);
  const [loadingPlanOfCare, setLoadingPlanOfCare] = React.useState<boolean>(true);
  const [loadingPatient, setLoadingPatient] = React.useState<boolean>(true);
  const [loadingAuthorization, setLoadingAuthorization] = React.useState<boolean>(true);

  const [saving, setSaving] = React.useState<boolean>(false);
  const [settingsOpen, setSettingsOpen] = React.useState<boolean>(false);
  const [authorizationDialogOpen, setAuthorizationDialogOpen] = React.useState<boolean>(false);
  const [authorizationDebugExpanded, setAuthorizationDebugExpanded] = React.useState<boolean>(false);
  const [clearingCache, setClearingCache] = React.useState<boolean>(false);

  // Provider & Provider Group state
  const webptProviders = useSelector((state: RootState) => state.webptIntegration.providers);
  const providerGroups = useSelector((state: RootState) => state.providers.providerGroups);
  const [selectedProviders, setSelectedProviders] = React.useState<string[]>([]);
  const [providerGroupId, setProviderGroupId] = React.useState<string | undefined>(undefined);

  const providerOptions = webptProviders.map((provider) => ({
    value: provider.id,
    label: provider.name,
  }));
  const providerGroupOptions = providerGroups.map((group) => ({
    value: group.providerGroupId,
    label: group.name,
  }));

  const handleProviderGroupChange = (newProviderGroupId: string) => {
    setProviderGroupId(newProviderGroupId || undefined);
    if (newProviderGroupId) {
      const group = providerGroups?.find((g) => g.providerGroupId === newProviderGroupId);
      if (group) {
        setSelectedProviders(group.providerIds);
      }
    }
  };

  // Sync local state when activePlanOfCare loads
  React.useEffect(() => {
    if (activePlanOfCare) {
      setSelectedProviders(activePlanOfCare.webptProviderIds || []);
      setProviderGroupId(activePlanOfCare.providerGroupId || undefined);
    }
  }, [activePlanOfCare?.activePlanOfCareId]);

  // Fetch providers and provider groups
  React.useEffect(() => {
    if (user?.token && organizationId) {
      dispatch(fetchWebptProviders({ token: user.token, organizationId }));
      dispatch(fetchProviderGroups(user.token));
    }
  }, [user?.token, organizationId]);

  React.useEffect(() => {
    if (user?.token && !insurances.length) {
      dispatch(fetchInsurances(user.token));
    }
  }, [user?.token, insurances.length]);

  const [sendingPocLink, setSendingPocLink] = React.useState<boolean>(false);
  const [showNoUnscheduledVisitsDialog, setShowNoUnscheduledVisitsDialog] = React.useState<boolean>(false);
  const [showBulkOverrideDialog, setShowBulkOverrideDialog] = React.useState<boolean>(false);
  const [bulkOverrideSource, setBulkOverrideSource] = React.useState<string>("all");
  const [bulkOverrideTarget, setBulkOverrideTarget] = React.useState<string>("");

  const contactPatientToBookVisits = async () => {
    if (!user?.token) return;
    setSendingPocLink(true);
    try {
      await api.post(`/bookings/plan-of-care/send`, user.token, {
        patientId: patientId,
        organizationId: organizationId,
        planOfCareId: planOfCareId,
        type: "update",
      });
      showMessage("Booking link sent to patient", "success");
    } catch (error) {
      console.error("Error sending poc link:", error);
      showMessage("Error sending poc link.", "error");
    } finally {
      setSendingPocLink(false);
    }
  };

  const handleSendBookingLinkClick = () => {
    // Check if there are no unscheduled visits
    if (activePlanOfCare && activePlanOfCare.appointmentsUnscheduled === 0) {
      setShowNoUnscheduledVisitsDialog(true);
    } else {
      contactPatientToBookVisits();
    }
  };

  const handleConfirmSendBookingLink = () => {
    setShowNoUnscheduledVisitsDialog(false);
    contactPatientToBookVisits();
  };

  const handleCancelSendBookingLink = () => {
    setShowNoUnscheduledVisitsDialog(false);
  };

  const handleBulkOverrideAppointmentType = async () => {
    if (!activePlanOfCare || !user?.token) return;
    const updatedPeriods = activePlanOfCare.activePlanOfCarePeriods.map((period) => ({
      ...period,
      activePlanOfCareAppointments: period.activePlanOfCareAppointments.map((appointment) => {
        if (appointment.status === "occurred" || appointment.status === "missed") {
          return appointment;
        }
        const matchesSource = (bulkOverrideSource === "no-type" && !appointment.appointmentTypeId) || appointment.appointmentTypeId === bulkOverrideSource;
        if (!matchesSource) return appointment;
        return { ...appointment, appointmentTypeId: bulkOverrideTarget };
      }),
    }));
    const updatedPlan = { ...activePlanOfCare, activePlanOfCarePeriods: updatedPeriods };
    setActivePlanOfCare(updatedPlan);
    setShowBulkOverrideDialog(false);

    setSaving(true);
    try {
      await api.put(`/plan-of-care/active/${activePlanOfCare.activePlanOfCareId}`, user.token, {
        ...updatedPlan,
        webptProviderIds: selectedProviders,
        providerGroupId: providerGroupId || null,
      });
      await fetchPatientPlanOfCare();
      showMessage("Upcoming visit types updated and saved.", "success");
    } catch (error) {
      console.error("Error saving plan of care after override:", error);
      showMessage("Visit types updated locally but failed to save.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleCreatePlanOfCareWeek = () => {
    setActivePlanOfCare((prev) => prev && createPlanOfCareWeek(prev, appointmentData, appointmentTypes));
  };

  const handlePrependPlanOfCareWeek = () => {
    setActivePlanOfCare((prev) => prev && prependPlanOfCareWeek(prev, appointmentData, appointmentTypes));
  };

  const handleUpdatePlanOfCareWeek = (week: CompleteActivePlanOfCarePeriod) => {
    const truncated = authorization && superOrganization?.autoTruncateAppointments && !activePlanOfCare?.preserve ? truncatePeriodToAuthorization(week, authorization) : week;
    setActivePlanOfCare((prev) => prev && updatePlanOfCareWeek(prev, truncated, appointmentData, appointmentTypes));
  };

  const handleDeletePlanOfCareWeek = (weekId: string) => {
    setActivePlanOfCare((prev) => prev && deletePlanOfCareWeek(prev, weekId, appointmentData, appointmentTypes));
  };

  const updateAppointmentData = (data: WebptPatientAppointment[]) => {
    setAppointmentData(data);
  };

  const recalculatePlanOfCare = () => {
    console.log("Recalculating plan of care...");
    if (!activePlanOfCare) return;
    if (!appointmentData) return;
    if (appointmentTypes.length === 0) return;
    console.log("Recalculating plan of care...got all data");
    let poc = activePlanOfCare;

    if (superOrganization?.autoExtendToWeeks) {
      poc = extendPlanOfCare(poc, appointmentData, appointmentTypes, DateTime.now().plus({ weeks: superOrganization?.numberOfWeeksToAutoExtend }).toISO());
    }

    if (authorization) {
      poc = addAuthorizationToPlanOfCare(poc, authorization);
      if (superOrganization?.autoTruncateAppointments && !poc.preserve) {
        poc = truncatePlanOfCareToAuthorization(poc, authorization);
      }
      if (superOrganization?.autoExtendToAuth) {
        poc = populatePlanOfCare(poc, appointmentData, appointmentTypes);
        poc = extendPlanOfCareToAuthorization(poc, appointmentData, appointmentTypes, authorization);
      }
      if (superOrganization?.autoExtendToPoc && authorization.endDate) {
        poc = extendPlanOfCare(poc, appointmentData, appointmentTypes, authorization.endDate);
      }
    }

    poc = populatePlanOfCare(poc, appointmentData, appointmentTypes);

    setActivePlanOfCare(poc);
  };

  React.useEffect(() => {
    console.log("Active plan of care changed, recalculating plan of care...");
    recalculatePlanOfCare();
  }, [activePlanOfCare?.activePlanOfCareId]);

  React.useEffect(() => {
    console.log("Authorization changed, recalculating plan of care...");
    recalculatePlanOfCare();
  }, [authorization]);

  async function fetchAppointmentTypes() {
    setLoadingAppointmentTypes(true);
    if (!user?.token) {
      setLoadingAppointmentTypes(false);
      return;
    }
    try {
      const response = await api.get(`/appointment-types`, user.token);
      const appointmentTypes = response.data as AppointmentType[];
      const sortedAppointmentTypes = appointmentTypes.sort((a, b) => a.name.localeCompare(b.name));
      setAppointmentTypes(sortedAppointmentTypes);

      const initialExamination = sortedAppointmentTypes.find((type) => type.name === "Initial Examination");
      setFirstVisit(initialExamination);
    } catch (error) {
      console.error("Error fetching appointment types:", error);
      showMessage("Error fetching appointment types.", "error");
      setAppointmentTypes([]);
    } finally {
      setLoadingAppointmentTypes(false);
    }
  }

  async function fetchAppointmentData() {
    setLoadingAppointmentData(true);
    if (!user?.token) {
      setLoadingAppointmentData(false);
      return;
    }
    try {
      const response = await api.get(`/webpt/patients/${patientId}/appointments`, user.token);
      updateAppointmentData(response.data);
    } catch (error) {
      console.error("Error fetching appointment data:", error);
      showMessage("Error fetching appointment data.", "error");
      setAppointmentData([]);
    } finally {
      setLoadingAppointmentData(false);
    }
  }

  async function fetchPatient() {
    try {
      setLoadingPatient(true);
      await dispatch(getWaitlistPatient({ patientId, token: user?.token || "" }));
    } catch (error) {
      console.error("Error fetching patient:", error);
      showMessage("Error fetching patient.", "error");
    } finally {
      setLoadingPatient(false);
    }
  }

  async function fetchCaseStudies() {
    setLoadingCaseStudies(true);
    if (!user?.token || patientId === "" || !patient) {
      setLoadingCaseStudies(false);
      return;
    }
    try {
      const response = await api.get(`/webpt/patients/${patient[EHR.webpt]?.webptPatientId}`, user.token);
      const data = response.data;
      setCaseStudies(data.caseStudies);
    } catch (error) {
      console.error("Error fetching case studies:", error);
      showMessage("Error fetching case studies.", "error");
    } finally {
      setLoadingCaseStudies(false);
    }
  }

  async function fetchPatientPlanOfCare() {
    setLoadingPlanOfCare(true);
    if (!user?.token) {
      setLoadingPlanOfCare(false);
      return;
    }
    try {
      const response = await api.get(`/plan-of-care/active/${planOfCareId}`, user.token);
      const planOfCare = response.data as CompleteActivePlanOfCare;
      setActivePlanOfCare(populatePlanOfCare(planOfCare, appointmentData, appointmentTypes));
      setMostVisits(appointmentTypes.find((type) => type.appointmentTypeId === planOfCare.defaultAppointmentTypeId));
      setCaseStudyId(planOfCare.caseStudyId || 0);
    } catch (error) {
      console.error("Error fetching patient plan of care:", error);
      showMessage("Error fetching patient plan of care.", "error");
      setActivePlanOfCare(null);
    } finally {
      setLoadingPlanOfCare(false);
    }
  }

  async function fetchPatientAuthorization() {
    setLoadingAuthorization(true);
    if (!user?.token || !caseStudyId) {
      setLoadingAuthorization(false);
      return;
    }
    try {
      const response = await api.get(`/webpt/patients/${patientId}/authorization?caseStudyId=${caseStudyId}`, user.token);
      const data = response.data as WebptPlanOfCareMetadata;
      setAuthorization(data);
    } catch (error) {
      console.error("Error fetching patient authorization:", error);
      showMessage("Error fetching patient authorization.", "error");
      setAuthorization(null);
    } finally {
      setLoadingAuthorization(false);
    }
  }

  async function clearPatientCache() {
    if (!user?.token) return;
    setClearingCache(true);
    try {
      await api.delete(`/webpt/patients/${patient[EHR.webpt]?.webptPatientId}/cache`, user.token);
      await fetchPatientAuthorization();
      showMessage("Cache cleared successfully.", "success");
    } catch (error) {
      console.error("Error clearing patient cache:", error);
      showMessage("Error clearing patient cache.", "error");
    } finally {
      setClearingCache(false);
    }
  }

  React.useEffect(() => {
    const fetchAll = async () => {
      await fetchPatient();
      await fetchAppointmentData();
      await fetchAppointmentTypes();
    };

    if (user?.token) {
      fetchAll();
    }
  }, [user?.token]);

  React.useEffect(() => {
    fetchCaseStudies();
  }, [user?.token, patientId, patient?.[EHR.webpt]?.webptPatientId]);

  React.useEffect(() => {
    if (user?.token /* && appointmentData.length > 0 */ && appointmentTypes.length > 0) {
      fetchPatientPlanOfCare();
    }
  }, [user?.token, appointmentData.length, appointmentTypes.length]);

  React.useEffect(() => {
    const caseStudy = caseStudies.find((study) => study.id === caseStudyId);
    if (!caseStudy) {
      console.log("Case study not found for ID:", caseStudyId, caseStudies);
      return;
    }
    console.log("Found case study:", caseStudy);
    setActivePlanOfCare((prev) =>
      prev ? populatePlanOfCare({ ...prev, caseStudyId: caseStudyId, discharged: !caseStudy.isActive, caseStudyName: caseStudy.name }, appointmentData, appointmentTypes) : prev,
    );
    fetchPatientAuthorization();
  }, [user?.token, caseStudyId, caseStudies.length]);

  const updatePlanOfCare = async () => {
    if (!user?.token || !activePlanOfCare) return;
    setSaving(true);
    try {
      await api.put(`/plan-of-care/active/${activePlanOfCare.activePlanOfCareId}`, user.token, {
        ...activePlanOfCare,
        webptProviderIds: selectedProviders,
        providerGroupId: providerGroupId || null,
      });
      await fetchPatientPlanOfCare();
      showMessage("Plan of care updated successfully.", "success");
    } catch (error) {
      console.error("Error updating plan of care:", error);
      showMessage("Error updating plan of care.", "error");
    } finally {
      setSaving(false);
    }
  };

  const deleteActivePlanOfCare = async (activePlanOfCareId: string) => {
    if (!user?.token) return;
    await api.delete(`/plan-of-care/active/${activePlanOfCareId}`, user.token);
    navigate(`/dashboard/patients/${patientId}`);
  };

  const extraAppointments = activePlanOfCare?.extraAppointments || [];

  const isLoading = loadingAppointmentTypes || loadingAppointmentData || loadingCaseStudies || loadingPlanOfCare || loadingPatient || loadingAuthorization;

  return (
    <Box
      sx={{
        marginTop: { xs: "75px", sm: 0 },
        color: "#FFF",
      }}
    >
      <SignoutHeader />

      {/* Page Header */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Link to={`/dashboard/patients/${patientId}`} style={{ textDecoration: "none" }}>
            <SecondaryButton
              sx={{
                display: { xs: "none", lg: "flex" },
                alignItems: "center",
                marginRight: "16px",
                mb: 1,
              }}
            >
              <ArrowBackIcon sx={{ marginRight: "10px" }} /> Back
            </SecondaryButton>
          </Link>
          <Typography variant="h4" fontWeight="700" sx={{ mb: 0.5, mt: 2 }}>
            Plan of Care
          </Typography>
          <Typography variant="body1" sx={{ color: Colors.info }}>
            {patient ? `${patient.patient.firstName} ${patient.patient.lastName}` : "Loading patient..."}
          </Typography>
        </Box>

        {!isLoading && activePlanOfCare && (
          <Box sx={{ display: "flex", gap: 1 }}>
            {hasWebptIntegration && ehrId && caseStudyId && (
              <Button
                variant="outlined"
                component="a"
                href={`https://app.webpt.com/patient/edit/index/PatientID/${ehrId}`}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<OpenInNewIcon sx={{ fontSize: 18 }} />}
                sx={{
                  textTransform: "none",
                  fontSize: "14px",
                  fontWeight: 600,
                }}
              >
                View in WebPT
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={() => setAuthorizationDialogOpen(true)}
              startIcon={<InfoOutlinedIcon sx={{ fontSize: 18 }} />}
              sx={{
                textTransform: "none",
                fontSize: "14px",
                fontWeight: 600,
              }}
            >
              Authorization
            </Button>
            <Button
              variant="outlined"
              onClick={() => setSettingsOpen(true)}
              startIcon={<SettingsIcon sx={{ fontSize: 18 }} />}
              sx={{
                textTransform: "none",
                fontSize: "14px",
                fontWeight: 600,
              }}
            >
              Plan Settings
            </Button>
          </Box>
        )}
      </Box>

      {/* Loading States */}
      {isLoading && (
        <Box sx={{ mt: 4, display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
          <CircularProgress size={28} />
          <Typography sx={{ fontSize: "13px", color: "rgba(255,255,255,0.5)" }}>
            {"Loading " + [
              loadingPatient && "patient",
              loadingPlanOfCare && "plan of care",
              loadingAppointmentTypes && "appointment types",
              loadingAppointmentData && "visit history",
              loadingCaseStudies && "case studies",
              loadingAuthorization && "authorization",
            ].filter(Boolean).join(", ") + "..."}
          </Typography>
        </Box>
      )}

      {!isLoading && activePlanOfCare && (
        <>
          {/* Two Column Layout - Analytics (Settings moved to modal) */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.2fr)" },
              gap: 3,
              mb: 4,
            }}
          >
            {/* Left Column - Analytics */}
            <Box
              sx={{
                backgroundColor: "rgba(255, 255, 255, 0.04)",
                border: `1px solid ${Colors.border}`,
                borderRadius: "12px",
                padding: "24px",
                height: "fit-content",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "space-between",
                  mb: 2.5,
                  gap: 2,
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight="600"
                  sx={{
                    color: "#FFF",
                  }}
                >
                  {activePlanOfCare.caseStudyName}
                </Typography>
                <Box sx={{ textAlign: "right" }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "rgba(255, 255, 255, 0.5)",
                      fontSize: "11px",
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      display: "block",
                    }}
                  >
                    Plan window
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "rgba(255, 255, 255, 0.8)",
                      fontSize: "13px",
                      fontWeight: 500,
                    }}
                  >
                    {activePlanOfCare.activePlanOfCarePeriods.length} weeks · {activePlanOfCare.totalAppointments || 0} visits
                  </Typography>
                </Box>
              </Box>

              {/* Key Metrics - minimal cards */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                {/* Completion Summary + Plan window */}
                <Box>
                  <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 1 }}>
                    {activePlanOfCare.discharged && (
                      <Typography variant="h4" fontWeight="600" sx={{ color: "#FFF", fontSize: "28px", lineHeight: 1 }}>
                        Discharged
                      </Typography>
                    )}
                    {!activePlanOfCare.discharged && (
                      <>
                        <Typography variant="h4" fontWeight="600" sx={{ color: "#FFF", fontSize: "28px", lineHeight: 1 }}>
                          {(() => {
                            const completed = activePlanOfCare?.appointmentsOccurred || 0;
                            const extra = activePlanOfCare?.totalExtraAppointments || 0;
                            const scheduled = activePlanOfCare?.appointmentsScheduled || 0;
                            const unscheduled = activePlanOfCare?.appointmentsUnscheduled || 0;
                            const unauthorized = activePlanOfCare?.appointmentsUnauthorized || 0;
                            const total = completed + extra + scheduled + unscheduled + unauthorized;
                            return total > 0 ? Math.round(((completed + extra) / total) * 100) : 0;
                          })()}
                          %
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "rgba(255, 255, 255, 0.5)",
                            fontSize: "13px",
                            fontWeight: 500,
                          }}
                        >
                          completed
                        </Typography>
                      </>
                    )}
                  </Box>
                  <Box
                    sx={{
                      height: "4px",
                      backgroundColor: "rgba(255, 255, 255, 0.08)",
                      borderRadius: "2px",
                      overflow: "hidden",
                      position: "relative",
                      mb: 1.25,
                    }}
                  >
                    <Box
                      sx={{
                        height: "100%",
                        width: `${(() => {
                          const completed = activePlanOfCare?.appointmentsOccurred || 0;
                          const extra = activePlanOfCare?.totalExtraAppointments || 0;
                          const scheduled = activePlanOfCare?.appointmentsScheduled || 0;
                          const unscheduled = activePlanOfCare?.appointmentsUnscheduled || 0;
                          const unauthorized = activePlanOfCare?.appointmentsUnauthorized || 0;
                          const total = completed + extra + scheduled + unscheduled + unauthorized;
                          return total > 0 ? Math.round(((completed + extra) / total) * 100) : 0;
                        })()}%`,
                        backgroundColor: activePlanOfCare.discharged ? Colors.error : Colors.success,
                        borderRadius: "2px",
                        transition: "width 0.3s ease",
                      }}
                    />
                  </Box>
                  {/* Intentionally no extra description under the completion bar for a cleaner, Stripe-like look */}
                </Box>

                {/* Status cards grid */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "repeat(4, minmax(0, 1fr))" },
                    gap: 1.5,
                  }}
                >
                  {/* Completed Card */}
                  <Box
                    sx={{
                      backgroundColor: "rgba(34, 182, 114, 0.06)",
                      borderRadius: "10px",
                      border: `1px solid rgba(34, 182, 114, 0.5)`,
                      px: 2,
                      py: 1.75,
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box
                          sx={{
                            width: "8px",
                            height: "8px",
                            borderRadius: "50%",
                            backgroundColor: Colors.success,
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#FFF",
                            fontSize: "14px",
                            fontWeight: 500,
                          }}
                        >
                          Completed
                        </Typography>
                      </Box>
                      <Typography variant="body2" fontWeight="600" sx={{ color: "#FFF", fontSize: "15px" }}>
                        {activePlanOfCare.appointmentsOccurred || 0}
                      </Typography>
                    </Box>
                    <Box sx={{ mt: 0.5 }}>
                      {(() => {
                        const completedAppointments = activePlanOfCare.activePlanOfCarePeriods
                          .flatMap((p) => p.activePlanOfCareAppointments)
                          .filter((a) => a.status === "occurred" && a.appointmentDate);

                        if (completedAppointments.length === 0) {
                          return (
                            <Typography
                              variant="caption"
                              sx={{
                                color: "rgba(255, 255, 255, 0.6)",
                                fontSize: "11px",
                              }}
                            >
                              No completed visits yet.
                            </Typography>
                          );
                        }

                        const sorted = completedAppointments.sort((a, b) => DateTime.fromISO(a.appointmentDate || "").toMillis() - DateTime.fromISO(b.appointmentDate || "").toMillis());
                        const first = sorted[0];
                        const last = sorted[sorted.length - 1];

                        return (
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "rgba(255, 255, 255, 0.6)",
                                fontSize: "11px",
                              }}
                            >
                              First: {first.appointmentDate ? DateTime.fromISO(first.appointmentDate).toFormat("MM/dd/yyyy") : "-"}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: "rgba(255, 255, 255, 0.6)",
                                fontSize: "11px",
                              }}
                            >
                              Most recent: {last.appointmentDate ? DateTime.fromISO(last.appointmentDate).toFormat("MM/dd/yyyy") : "-"}
                            </Typography>
                          </Box>
                        );
                      })()}
                    </Box>
                  </Box>

                  {/* Scheduled Card with next appointment */}
                  <Box
                    sx={{
                      backgroundColor: "rgba(42, 127, 243, 0.06)",
                      borderRadius: "10px",
                      border: `1px solid rgba(42, 127, 243, 0.5)`,
                      px: 2,
                      py: 1.75,
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.75 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box
                          sx={{
                            width: "8px",
                            height: "8px",
                            borderRadius: "50%",
                            backgroundColor: Colors.primary,
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#FFF",
                            fontSize: "14px",
                            fontWeight: 500,
                          }}
                        >
                          Scheduled
                        </Typography>
                      </Box>
                      <Typography variant="body2" fontWeight="600" sx={{ color: "#FFF", fontSize: "15px" }}>
                        {activePlanOfCare.appointmentsScheduled || 0}
                      </Typography>
                    </Box>
                    {(() => {
                      const scheduledAppointments = activePlanOfCare.activePlanOfCarePeriods
                        .flatMap((p) => p.activePlanOfCareAppointments)
                        .filter((a) => a.status === "scheduled" && a.appointmentDate);

                      if (scheduledAppointments.length === 0) {
                        return (
                          <Typography
                            variant="caption"
                            sx={{
                              color: "rgba(255, 255, 255, 0.6)",
                              fontSize: "11px",
                            }}
                          >
                            No upcoming visits scheduled.
                          </Typography>
                        );
                      }

                      const next = scheduledAppointments.sort((a, b) => DateTime.fromISO(a.appointmentDate || "").toMillis() - DateTime.fromISO(b.appointmentDate || "").toMillis())[0];

                      return (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "rgba(255, 255, 255, 0.8)",
                            fontSize: "11px",
                          }}
                        >
                          Next: {next.appointmentDate ? DateTime.fromISO(next.appointmentDate).toFormat("MM/dd/yyyy") : "-"}
                        </Typography>
                      );
                    })()}
                  </Box>

                  {/* Unscheduled Card with next unscheduled week and booking link */}
                  <Box
                    sx={{
                      backgroundColor: "rgba(245, 178, 0, 0.06)",
                      borderRadius: "10px",
                      border: `1px solid rgba(245, 178, 0, 0.5)`,
                      px: 2,
                      py: 1.75,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box
                          sx={{
                            width: "8px",
                            height: "8px",
                            borderRadius: "50%",
                            backgroundColor: Colors.warning,
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#FFF",
                            fontSize: "14px",
                            fontWeight: 500,
                          }}
                        >
                          Unscheduled
                        </Typography>
                      </Box>
                      <Typography variant="body2" fontWeight="600" sx={{ color: "#FFF", fontSize: "15px" }}>
                        {activePlanOfCare.appointmentsUnscheduled || 0}
                      </Typography>
                    </Box>
                    {(() => {
                      const unscheduledByWeek = activePlanOfCare.activePlanOfCarePeriods
                        .map((p) => ({
                          period: p,
                          unscheduledCount: p.activePlanOfCareAppointments.filter((a) => a.status === "unscheduled").length,
                        }))
                        .filter((w) => w.unscheduledCount > 0);

                      if (unscheduledByWeek.length === 0) {
                        return (
                          <Typography
                            variant="caption"
                            sx={{
                              color: "rgba(255, 255, 255, 0.7)",
                              fontSize: "11px",
                            }}
                          >
                            No unscheduled visits remaining.
                          </Typography>
                        );
                      }

                      const nextWeek = unscheduledByWeek.sort((a, b) => DateTime.fromISO(a.period.startDate).toMillis() - DateTime.fromISO(b.period.startDate).toMillis())[0];

                      return (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "rgba(255, 255, 255, 0.8)",
                            fontSize: "11px",
                          }}
                        >
                          Next unscheduled week: {DateTime.fromISO(nextWeek.period.startDate).toFormat("MM/dd")} – {DateTime.fromISO(nextWeek.period.endDate).toFormat("MM/dd")}
                        </Typography>
                      );
                    })()}
                    <Button
                      variant="contained"
                      onClick={handleSendBookingLinkClick}
                      disabled={!activePlanOfCare || sendingPocLink}
                      sx={{
                        mt: 0.5,
                        textTransform: "none",
                        backgroundColor: Colors.primary,
                        color: "#FFF",
                        fontSize: "13px",
                        fontWeight: 500,
                        boxShadow: "none",
                        "&:hover": {
                          backgroundColor: Colors.primary,
                          opacity: 0.9,
                          boxShadow: "none",
                        },
                        "&:disabled": {
                          backgroundColor: "rgba(255, 255, 255, 0.08)",
                          color: "rgba(255, 255, 255, 0.4)",
                        },
                      }}
                    >
                      {sendingPocLink ? "Sending..." : "Send booking link"}
                    </Button>
                  </Box>
                  {/* Unauthorized Card (in same row, only if relevant) */}
                  {(activePlanOfCare.appointmentsUnauthorized || 0) > 0 && (
                    <Box
                      sx={{
                        backgroundColor: "rgba(102, 102, 102, 0.1)",
                        borderRadius: "10px",
                        border: `1px solid ${Colors.grey2}`,
                        px: 2,
                        py: 1.75,
                      }}
                    >
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.75 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box
                            sx={{
                              width: "8px",
                              height: "8px",
                              borderRadius: "50%",
                              backgroundColor: Colors.grey2,
                            }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              color: "#FFF",
                              fontSize: "14px",
                              fontWeight: 500,
                            }}
                          >
                            Unauthorized
                          </Typography>
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#FFF",
                            fontSize: "15px",
                            fontWeight: 600,
                          }}
                        >
                          {activePlanOfCare.appointmentsUnauthorized || 0}
                        </Typography>
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "rgba(255, 255, 255, 0.7)",
                          fontSize: "11px",
                        }}
                      >
                        Visits currently outside authorization limits.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Weekly Schedule */}
          <Box sx={{ mb: 3 }}>
            {/* Header with Action Buttons */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Typography variant="h6" fontWeight="600">
                Weekly Schedule
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  gap: 1.5,
                  flexWrap: "wrap",
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() => {
                    const upcoming = activePlanOfCare?.activePlanOfCarePeriods.flatMap((p) => p.activePlanOfCareAppointments).filter((a) => a.status !== "occurred" && a.status !== "missed") ?? [];
                    const firstTypeId = upcoming.find((a) => a.appointmentTypeId)?.appointmentTypeId;
                    const hasNoType = upcoming.some((a) => !a.appointmentTypeId);
                    setBulkOverrideSource(firstTypeId ?? (hasNoType ? "no-type" : ""));
                    setBulkOverrideTarget(activePlanOfCare?.defaultAppointmentTypeId || "");
                    setShowBulkOverrideDialog(true);
                  }}
                  sx={{
                    textTransform: "none",
                    fontSize: "14px",
                    fontWeight: 600,
                  }}
                >
                  Override Visit Types
                </Button>
                <DeleteButton
                  onDelete={() => {
                    if (activePlanOfCare) {
                      deleteActivePlanOfCare(activePlanOfCare.activePlanOfCareId);
                    }
                  }}
                />
                <Tooltip title={activePlanOfCare?.preserve ? "Remove protection from auto-adjustments" : "Protect entire plan from auto-adjustments"} arrow>
                  <IconButton
                    onClick={() => setActivePlanOfCare((prev) => prev && { ...prev, preserve: !prev.preserve })}
                    sx={{
                      color: activePlanOfCare?.preserve ? Colors.primary : "rgba(255,255,255,0.3)",
                      "&:hover": { color: Colors.primary },
                    }}
                  >
                    {activePlanOfCare?.preserve ? <LockIcon /> : <LockOpenIcon />}
                  </IconButton>
                </Tooltip>
                <Button
                  variant="contained"
                  onClick={updatePlanOfCare}
                  disabled={!activePlanOfCare || saving}
                  sx={{
                    textTransform: "none",
                    px: 3,
                    fontSize: "14px",
                    fontWeight: 600,
                  }}
                >
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
              </Box>
            </Box>
            <Box>
              <PlanOfCareWeeksWithCollapse
                activePlanOfCare={activePlanOfCare}
                appointmentTypes={appointmentTypes}
                handleUpdatePlanOfCareWeek={handleUpdatePlanOfCareWeek}
                handleDeletePlanOfCareWeek={handleDeletePlanOfCareWeek}
                defaultAppointmentTypeId={mostVisits?.appointmentTypeId || ""}
                editable={true}
                handlePrependPlanOfCareWeek={handlePrependPlanOfCareWeek}
                handleCreatePlanOfCareWeek={handleCreatePlanOfCareWeek}
                authorizationsPerSource={authorization?.authorizationsPerSource}
                authorizationSourcePriority={authorizationSourcePriority}
              />

              {extraAppointments.map((appointment) => {
                const activePlanOfCareAppointmentId = uuidv4();
                let appointmentTypeId = appointmentTypes.find((type) => type.webptShortCode === appointment.AptType)?.appointmentTypeId || "";
                if (appointment.AptType === "CT") appointmentTypeId = "";
                const pocAppointment: ActivePlanOfCareAppointment = {
                  activePlanOfCareId: activePlanOfCare.activePlanOfCareId,
                  activePlanOfCarePeriodId: "",
                  activePlanOfCareAppointmentId: activePlanOfCareAppointmentId,
                  superOrganizationId: activePlanOfCare.superOrganizationId,
                  organizationId: activePlanOfCare.organizationId,
                  patientId: patientId,

                  appointmentTypeId: appointmentTypeId,
                  appointmentDate: appointment.startString,
                  status: "scheduled",
                };

                return (
                  <AppointmentCard
                    key={activePlanOfCareAppointmentId}
                    appointment={pocAppointment}
                    appointmentNumber={0}
                    appointmentTypes={appointmentTypes}
                    updatePlanOfCareAppointment={() => {}}
                    handleDeleteAppointment={() => {}}
                    editable={false}
                    isExtra={true}
                  />
                );
              })}
            </Box>
          </Box>

          {/* Authorization Details Modal */}
          <Dialog
            open={authorizationDialogOpen}
            onClose={() => setAuthorizationDialogOpen(false)}
            fullWidth
            maxWidth="xs"
            PaperProps={{
              sx: {
                backgroundColor: "#1a1a2e",
                border: `1px solid ${Colors.border}`,
                borderRadius: "12px",
              },
            }}
          >
            <DialogTitle sx={{ color: "#FFF", fontWeight: 600, pb: 1 }}>Plan of Care Details</DialogTitle>
            <DialogContent>
              {authorization && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  {/* POC Information */}
                  <Box>
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "11px", fontWeight: 600 }}>
                      Plan of Care (Pulled from Documentation)
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, mt: 1.25 }}>
                      {[
                        { label: "Case Study", value: authorization.caseStudyName },

                        { label: "Total Visits", value: authorization.numVisits },
                        { label: "Document Date", value: authorization.startDate || "—" },
                        { label: "Parsed End Date", value: authorization.endDate || "—" },
                        { label: "Number of Weeks Left", value: authorization.numWeeks },
                        { label: "Written Frequency", value: authorization.rawFrequency || "—" },
                        { label: "Parsed Frequency", value: `${authorization.numPerWeek}x/week` },
                      ].map(({ label, value }) => (
                        <Box key={label} sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.5)", fontSize: "13px" }}>
                            {label}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#FFF", fontWeight: 500, fontSize: "13px" }}>
                            {String(value)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>

                  <Box sx={{ height: "1px", backgroundColor: Colors.border }} />

                  {/* Authorization Information */}
                  <Box>
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "11px", fontWeight: 600 }}>
                      Authorization
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, mt: 1.25 }}>
                      {[
                        { label: "Authorized Visits", value: authorization.totalAuthorizedVisits },
                        { label: "Used Visits", value: authorization.usedAuthorizedVisits },
                        { label: "Remaining Visits", value: authorization.totalAuthorizedVisits - authorization.usedAuthorizedVisits },
                        { label: "Future Scheduled", value: authorization.numFutureScheduledVisits },
                        { label: "Expiration Date", value: authorization.expirationDate || "—" },
                      ].map(({ label, value }) => (
                        <Box key={label} sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.5)", fontSize: "13px" }}>
                            {label}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#FFF", fontWeight: 500, fontSize: "13px" }}>
                            {String(value)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>

                  {authorization.authorizationsPerSource && (
                    <>
                      <Box sx={{ height: "1px", backgroundColor: Colors.border }} />
                      <Box>
                        <Box
                          sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", userSelect: "none" }}
                          onClick={() => setAuthorizationDebugExpanded((v) => !v)}
                        >
                          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "11px", fontWeight: 600 }}>
                            Authorizations Per Source
                          </Typography>
                          <ExpandMoreIcon
                            sx={{ color: "rgba(255,255,255,0.4)", fontSize: 18, transition: "transform 0.2s", transform: authorizationDebugExpanded ? "rotate(180deg)" : "rotate(0deg)" }}
                          />
                        </Box>
                        {authorizationDebugExpanded && (
                          <Box
                            component="pre"
                            sx={{
                              mt: 1.25,
                              p: 1.5,
                              backgroundColor: "rgba(0,0,0,0.3)",
                              borderRadius: "8px",
                              fontSize: "11px",
                              color: "rgba(255,255,255,0.6)",
                              overflowX: "auto",
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-all",
                              fontFamily: "monospace",
                              margin: 0,
                            }}
                          >
                            {JSON.stringify(authorization.authorizationsPerSource, null, 2)}
                          </Box>
                        )}
                      </Box>
                    </>
                  )}
                </Box>
              )}
              {!authorization && !loadingAuthorization && (
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.5)", fontSize: "13px" }}>
                  Authorization details are unavailable. Try refreshing.
                </Typography>
              )}
              {loadingAuthorization && (
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.5)", fontSize: "13px" }}>
                  Loading authorization...
                </Typography>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button
                onClick={clearPatientCache}
                disabled={clearingCache}
                variant="outlined"
                sx={{
                  textTransform: "none",
                  color: "rgba(255,255,255,0.85)",
                  borderColor: "rgba(255,255,255,0.25)",
                  "&:hover": { borderColor: "rgba(255,255,255,0.5)", backgroundColor: "rgba(255,255,255,0.05)" },
                }}
              >
                {clearingCache ? "Refreshing..." : "Refresh Authorization"}
              </Button>
              <Button onClick={() => setAuthorizationDialogOpen(false)} sx={{ textTransform: "none", color: "rgba(255,255,255,0.7)" }}>
                Close
              </Button>
            </DialogActions>
          </Dialog>

          {/* Settings Modal */}
          <Dialog
            open={settingsOpen}
            onClose={() => setSettingsOpen(false)}
            fullWidth
            maxWidth="sm"
            PaperProps={{
              sx: {
                backgroundColor: Colors.bg3,
                borderRadius: "16px",
                border: `1px solid ${Colors.border}`,
              },
            }}
          >
            <DialogTitle
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                pb: 1,
              }}
            >
              <Box>
                <Typography variant="h6" fontWeight="600" sx={{ fontSize: "16px", color: "#FFF" }}>
                  Plan settings
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "rgba(255, 255, 255, 0.6)",
                    fontSize: "11px",
                  }}
                >
                  Configure how visits are generated and constrained for this plan.
                </Typography>
              </Box>
            </DialogTitle>
            <DialogContent
              sx={{
                pt: 1,
              }}
            >
              {/* Case Study */}
              <Box sx={{ mb: 2.5, pb: 2.5, borderBottom: `1px solid ${Colors.border}` }}>
                <Typography
                  variant="body2"
                  fontWeight="600"
                  sx={{
                    mb: 0.5,
                    fontSize: "13px",
                    color: "#FFF",
                  }}
                >
                  Case Study
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "rgba(255, 255, 255, 0.5)",
                    fontSize: "11px",
                    lineHeight: 1.4,
                    display: "block",
                    mb: 1,
                  }}
                >
                  Patient case tracking appointments.
                </Typography>
                <Select
                  value={activePlanOfCare?.caseStudyId || 0}
                  onChange={(e) => setCaseStudyId(e.target.value as number)}
                  displayEmpty
                  fullWidth
                  size="small"
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                    borderRadius: "8px",
                    color: "#FFF",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: Colors.border,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: Colors.border,
                    },
                  }}
                >
                  {caseStudies.map((study) => (
                    <MenuItem key={study.id} value={study.id}>
                      {study.name}
                    </MenuItem>
                  ))}
                </Select>
                {activePlanOfCare?.discharged && (
                  <Box
                    sx={{
                      mt: 1.5,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      backgroundColor: "rgba(238, 38, 38, 0.1)",
                      border: `1px solid rgba(238, 38, 38, 0.3)`,
                      borderRadius: "6px",
                      padding: "8px 12px",
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight="600"
                      sx={{
                        color: Colors.error,
                        fontSize: "12px",
                      }}
                    >
                      Patient Discharged
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Default Appointment Type */}
              <Box sx={{ mb: 2.5, pb: 2.5, borderBottom: `1px solid ${Colors.border}` }}>
                <Typography
                  variant="body2"
                  fontWeight="600"
                  sx={{
                    mb: 0.5,
                    fontSize: "13px",
                    color: "#FFF",
                  }}
                >
                  Default Appointment Type
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "rgba(255, 255, 255, 0.5)",
                    fontSize: "11px",
                    lineHeight: 1.4,
                    display: "block",
                    mb: 1,
                  }}
                >
                  Used when adding new visits.
                </Typography>
                <Select
                  value={mostVisits?.appointmentTypeId || ""}
                  onChange={(e) => {
                    const selectedType = appointmentTypes.find((type) => type.appointmentTypeId === e.target.value);
                    if (selectedType) {
                      handleSetMostVisits(selectedType);
                    }
                  }}
                  displayEmpty
                  fullWidth
                  size="small"
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                    borderRadius: "8px",
                    color: "#FFF",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: Colors.border,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: Colors.border,
                    },
                  }}
                >
                  {filterAppointmentTypesForPicker(appointmentTypes, mostVisits?.appointmentTypeId, organization?.organizationId).map((type) => (
                    <MenuItem key={type.appointmentTypeId} value={type.appointmentTypeId}>
                      {type.name}
                    </MenuItem>
                  ))}
                </Select>
              </Box>

              {/* Provider Group */}
              {providerGroupOptions.length > 0 && (
                <Box sx={{ mb: 2.5, pb: 2.5, borderBottom: `1px solid ${Colors.border}` }}>
                  <Typography
                    variant="body2"
                    fontWeight="600"
                    sx={{
                      mb: 0.5,
                      fontSize: "13px",
                      color: "#FFF",
                    }}
                  >
                    Provider Group
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "rgba(255, 255, 255, 0.5)",
                      fontSize: "11px",
                      lineHeight: 1.4,
                      display: "block",
                      mb: 1,
                    }}
                  >
                    Assign a provider group to restrict scheduling to those providers.
                  </Typography>
                  <Select
                    value={providerGroupId || ""}
                    onChange={(e) => handleProviderGroupChange(e.target.value)}
                    displayEmpty
                    fullWidth
                    size="small"
                    sx={{
                      backgroundColor: "rgba(255, 255, 255, 0.05)",
                      borderRadius: "8px",
                      color: "#FFF",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: Colors.border,
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: Colors.border,
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em style={{ color: "rgba(255, 255, 255, 0.5)" }}>None</em>
                    </MenuItem>
                    {providerGroupOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              )}

              {/* Providers */}
              {providerOptions.length > 0 && (
                <Box sx={{ mb: 2.5, pb: 2.5, borderBottom: `1px solid ${Colors.border}` }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                    <Typography
                      variant="body2"
                      fontWeight="600"
                      sx={{
                        fontSize: "13px",
                        color: "#FFF",
                      }}
                    >
                      Providers
                    </Typography>
                    {selectedProviders.length === providerOptions.length ? (
                      <Button
                        onClick={() => setSelectedProviders([])}
                        sx={{
                          textTransform: "none",
                          fontSize: "11px",
                          color: "rgba(255, 255, 255, 0.5)",
                          minWidth: "auto",
                          p: 0,
                          "&:hover": { color: "#FFF" },
                        }}
                      >
                        Deselect All
                      </Button>
                    ) : (
                      <Button
                        onClick={() => setSelectedProviders(providerOptions.map((o) => o.value))}
                        sx={{
                          textTransform: "none",
                          fontSize: "11px",
                          color: "rgba(255, 255, 255, 0.5)",
                          minWidth: "auto",
                          p: 0,
                          "&:hover": { color: "#FFF" },
                        }}
                      >
                        Select All
                      </Button>
                    )}
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "rgba(255, 255, 255, 0.5)",
                      fontSize: "11px",
                      lineHeight: 1.4,
                      display: "block",
                      mb: 1,
                    }}
                  >
                    Select individual providers for this plan of care.
                  </Typography>
                  <Select
                    disabled={!!providerGroupId}
                    multiple
                    displayEmpty
                    value={selectedProviders}
                    onChange={(e) => setSelectedProviders(e.target.value as string[])}
                    renderValue={(selected) => {
                      if (selected.length === 0) {
                        return <Typography sx={{ color: "rgba(255, 255, 255, 0.4)", fontSize: "13px" }}>Select providers</Typography>;
                      }
                      return (selected as string[])
                        .map((value) => providerOptions.find((option) => option.value === value)?.label)
                        .filter(Boolean)
                        .join(", ");
                    }}
                    fullWidth
                    size="small"
                    sx={{
                      backgroundColor: "rgba(255, 255, 255, 0.05)",
                      borderRadius: "8px",
                      color: "#FFF",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: Colors.border,
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: Colors.border,
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 250,
                        },
                      },
                    }}
                  >
                    {providerOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        <Checkbox checked={selectedProviders.indexOf(option.value) > -1} size="small" />
                        <ListItemText primary={option.label} />
                      </MenuItem>
                    ))}
                  </Select>
                  {providerGroupId && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "rgba(255, 255, 255, 0.4)",
                        fontSize: "11px",
                        mt: 0.75,
                        display: "block",
                      }}
                    >
                      Providers are managed by the selected provider group.
                    </Typography>
                  )}
                </Box>
              )}

              {/* Frequency */}
              <Box sx={{ mb: 2.5, pb: 2.5, borderBottom: `1px solid ${Colors.border}` }}>
                <Typography
                  variant="body2"
                  fontWeight="600"
                  sx={{
                    mb: 0.5,
                    fontSize: "13px",
                    color: "#FFF",
                  }}
                >
                  Frequency (Current: {activePlanOfCare.frequency} visits/week)
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "rgba(255, 255, 255, 0.5)",
                    fontSize: "11px",
                    lineHeight: 1.4,
                    display: "block",
                    mb: 1,
                  }}
                >
                  Number of visits per week.
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    gap: 0.5,
                  }}
                >
                  {[1, 2, 3, 4, 5].map((num) => (
                    <Box
                      key={num}
                      onClick={() => {
                        handleSetFrequency(num);
                      }}
                      sx={{
                        minWidth: "36px",
                        textAlign: "center",
                        py: 0.5,
                        px: 1.25,
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "13px",
                        fontWeight: 600,
                        backgroundColor: (activePlanOfCare?.frequency || 0) === num ? Colors.primary : "rgba(255, 255, 255, 0.05)",
                        color: (activePlanOfCare?.frequency || 0) === num ? "#FFF" : "rgba(255, 255, 255, 0.5)",
                        border: "1px solid " + ((activePlanOfCare?.frequency || 0) === num ? Colors.primary : Colors.border),
                        transition: "all 0.15s ease",
                        "&:hover": {
                          backgroundColor: (activePlanOfCare?.frequency || 0) === num ? Colors.primary : "rgba(255, 255, 255, 0.08)",
                          borderColor: (activePlanOfCare?.frequency || 0) === num ? Colors.primary : Colors.border,
                        },
                      }}
                    >
                      {num}
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Gap Between Appointments */}
              <Box sx={{ mb: 2.5, pb: 2.5, borderBottom: `1px solid ${Colors.border}` }}>
                <Typography
                  variant="body2"
                  fontWeight="600"
                  sx={{
                    mb: 0.5,
                    fontSize: "13px",
                    color: "#FFF",
                  }}
                >
                  Gap Between Appointments
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "rgba(255, 255, 255, 0.5)",
                    fontSize: "11px",
                    lineHeight: 1.4,
                    display: "block",
                    mb: 1,
                  }}
                >
                  Minimum days between appointments.
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    gap: 0.5,
                  }}
                >
                  {[0, 1, 2, 3].map((num) => (
                    <Box
                      key={num}
                      onClick={() => {
                        setActivePlanOfCare((prev) => (prev ? { ...prev, gap: num } : prev));
                      }}
                      sx={{
                        minWidth: "36px",
                        textAlign: "center",
                        py: 0.5,
                        px: 1.25,
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "13px",
                        fontWeight: 600,
                        backgroundColor: (activePlanOfCare?.gap || 0) === num ? Colors.primary : "rgba(255, 255, 255, 0.05)",
                        color: (activePlanOfCare?.gap || 0) === num ? "#FFF" : "rgba(255, 255, 255, 0.5)",
                        border: "1px solid " + ((activePlanOfCare?.gap || 0) === num ? Colors.primary : Colors.border),
                        transition: "all 0.15s ease",
                        "&:hover": {
                          backgroundColor: (activePlanOfCare?.gap || 0) === num ? Colors.primary : "rgba(255, 255, 255, 0.08)",
                          borderColor: (activePlanOfCare?.gap || 0) === num ? Colors.primary : Colors.border,
                        },
                      }}
                    >
                      {num}
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Ignore Authorization */}
              <Box sx={{ mb: 2.5, pb: 2.5, borderBottom: `1px solid ${Colors.border}` }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
                  <Switch
                    checked={activePlanOfCare.ignoreAuthorization}
                    onChange={(event) => {
                      setActivePlanOfCare(
                        (prev) =>
                          prev &&
                          populatePlanOfCare(
                            {
                              ...prev,
                              ignoreAuthorization: event.target.checked,
                            },
                            appointmentData,
                            appointmentTypes,
                          ),
                      );
                    }}
                    size="small"
                  />
                  <Typography
                    variant="body2"
                    fontWeight="600"
                    sx={{
                      fontSize: "13px",
                      color: "#FFF",
                    }}
                  >
                    Ignore Visit Restrictions
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: "rgba(255, 255, 255, 0.5)",
                    fontSize: "11px",
                    lineHeight: 1.4,
                    display: "block",
                    ml: 5.5,
                  }}
                >
                  Allow scheduling beyond insurance limits.
                </Typography>
              </Box>

              {/* Ignore Documentation */}
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
                  <Switch
                    checked={!!activePlanOfCare.ignoreDocumentation}
                    onChange={(event) => {
                      setActivePlanOfCare((prev) => prev && { ...prev, ignoreDocumentation: event.target.checked });
                    }}
                    size="small"
                  />
                  <Typography
                    variant="body2"
                    fontWeight="600"
                    sx={{
                      fontSize: "13px",
                      color: "#FFF",
                    }}
                  >
                    Ignore Documentation
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: "rgba(255, 255, 255, 0.5)",
                    fontSize: "11px",
                    lineHeight: 1.4,
                    display: "block",
                    ml: 5.5,
                  }}
                >
                  Use the manually set frequency instead of the frequency from documentation. Auto-enabled when frequency is manually adjusted.
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions
              sx={{
                px: 3,
                py: 2,
                borderTop: `1px solid ${Colors.border}`,
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Button
                onClick={() => setSettingsOpen(false)}
                sx={{
                  textTransform: "none",
                  color: "rgba(255, 255, 255, 0.7)",
                  fontSize: "13px",
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={async () => {
                  await updatePlanOfCare();
                  setSettingsOpen(false);
                }}
                disabled={!activePlanOfCare || saving}
                sx={{
                  textTransform: "none",
                  fontSize: "13px",
                  fontWeight: 600,
                  px: 2.5,
                }}
              >
                {saving ? "Saving..." : "Save settings"}
              </Button>
            </DialogActions>
          </Dialog>

          {/* No Unscheduled Visits Confirmation Dialog */}
          <Dialog
            open={showNoUnscheduledVisitsDialog}
            onClose={handleCancelSendBookingLink}
            maxWidth="xs"
            fullWidth
            PaperProps={{
              sx: {
                backgroundColor: Colors.bg3,
                borderRadius: "16px",
                border: `1px solid ${Colors.border}`,
              },
            }}
          >
            <DialogTitle sx={{ color: "#FFF", fontWeight: 600, fontSize: "16px" }}>No Bookable Visits</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ color: "rgba(255, 255, 255, 0.7)", fontSize: "14px" }}>
                This patient does not have any bookable visits remaining. Do you still want to send the booking link?
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ padding: "12px", borderTop: `1px solid ${Colors.border}` }}>
              <Button
                onClick={handleCancelSendBookingLink}
                sx={{
                  textTransform: "none",
                  color: "rgba(255, 255, 255, 0.7)",
                  fontSize: "13px",
                  fontWeight: 500,
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleConfirmSendBookingLink}
                disabled={sendingPocLink}
                sx={{
                  textTransform: "none",
                  backgroundColor: Colors.primary,
                  color: "#FFF",
                  fontSize: "13px",
                  fontWeight: 600,
                  px: 2.5,
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: Colors.primary,
                    opacity: 0.9,
                    boxShadow: "none",
                  },
                }}
              >
                {sendingPocLink ? "Sending..." : "Send Anyway"}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Bulk Override Dialog */}
          {(() => {
            const upcomingAppointments = activePlanOfCare?.activePlanOfCarePeriods.flatMap((p) => p.activePlanOfCareAppointments).filter((a) => a.status !== "occurred" && a.status !== "missed") ?? [];

            const uniqueSourceTypeIds = Array.from(new Set(upcomingAppointments.map((a) => a.appointmentTypeId).filter(Boolean)));
            const hasNoTypeAppointments = upcomingAppointments.some((a) => !a.appointmentTypeId);

            const affectedCount = upcomingAppointments.filter((a) => (bulkOverrideSource === "no-type" ? !a.appointmentTypeId : a.appointmentTypeId === bulkOverrideSource)).length;

            const selectSx = {
              backgroundColor: "rgba(255, 255, 255, 0.05)",
              borderRadius: "8px",
              color: "#FFF",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: Colors.border },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: Colors.border },
              "& .MuiSvgIcon-root": { color: "rgba(255, 255, 255, 0.5)" },
            };

            return (
              <Dialog
                open={showBulkOverrideDialog}
                onClose={() => setShowBulkOverrideDialog(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                  sx: {
                    backgroundColor: Colors.bg3,
                    borderRadius: "16px",
                    border: `1px solid ${Colors.border}`,
                  },
                }}
              >
                <DialogTitle
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    pb: 1,
                  }}
                >
                  <Box>
                    <Typography variant="h6" fontWeight="600" sx={{ fontSize: "16px", color: "#FFF" }}>
                      Override visit types
                    </Typography>
                  </Box>
                </DialogTitle>

                <DialogContent sx={{ pt: 1 }}>
                  {/* Replace (source) */}
                  <Box sx={{ mb: 2.5, pb: 2.5, borderBottom: `1px solid ${Colors.border}` }}>
                    <Typography variant="body2" fontWeight="600" sx={{ mb: 0.5, fontSize: "13px", color: "#FFF" }}>
                      Replace
                    </Typography>
                    <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.5)", fontSize: "11px", lineHeight: 1.4, display: "block", mb: 1 }}>
                      The appointment type to replace across upcoming visits.
                    </Typography>
                    <Select value={bulkOverrideSource} onChange={(e) => setBulkOverrideSource(e.target.value)} displayEmpty fullWidth size="small" sx={selectSx}>
                      {uniqueSourceTypeIds.length === 0 && !hasNoTypeAppointments && (
                        <MenuItem value="" disabled>
                          <em style={{ color: "rgba(255, 255, 255, 0.45)" }}>No upcoming appointments</em>
                        </MenuItem>
                      )}
                      {hasNoTypeAppointments && <MenuItem value="no-type">No type</MenuItem>}
                      {uniqueSourceTypeIds.map((typeId) => {
                        const typeName = appointmentTypes.find((t) => t.appointmentTypeId === typeId)?.name ?? typeId;
                        return (
                          <MenuItem key={typeId} value={typeId}>
                            {typeName}
                          </MenuItem>
                        );
                      })}
                    </Select>
                    {affectedCount > 0 && (
                      <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.4)", fontSize: "11px", mt: 0.75, display: "block" }}>
                        {affectedCount} upcoming {affectedCount === 1 ? "visit" : "visits"} will be replaced.
                      </Typography>
                    )}
                  </Box>

                  {/* With (target) */}
                  <Box>
                    <Typography variant="body2" fontWeight="600" sx={{ mb: 0.5, fontSize: "13px", color: "#FFF" }}>
                      With
                    </Typography>
                    <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.5)", fontSize: "11px", lineHeight: 1.4, display: "block", mb: 1 }}>
                      The appointment type to assign in its place.
                    </Typography>
                    <Select value={bulkOverrideTarget} onChange={(e) => setBulkOverrideTarget(e.target.value)} displayEmpty fullWidth size="small" sx={selectSx}>
                      <MenuItem value="">
                        <em style={{ color: "rgba(255, 255, 255, 0.45)" }}>None (clear type)</em>
                      </MenuItem>
                      {filterAppointmentTypesForPicker(appointmentTypes, bulkOverrideTarget, organizationId).map((type) => (
                        <MenuItem key={type.appointmentTypeId} value={type.appointmentTypeId}>
                          {type.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>
                </DialogContent>

                <DialogActions
                  sx={{
                    px: 3,
                    py: 2,
                    borderTop: `1px solid ${Colors.border}`,
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <Button
                    onClick={() => setShowBulkOverrideDialog(false)}
                    sx={{
                      textTransform: "none",
                      color: "rgba(255, 255, 255, 0.7)",
                      fontSize: "13px",
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleBulkOverrideAppointmentType}
                    disabled={affectedCount === 0 || !bulkOverrideSource}
                    sx={{
                      textTransform: "none",
                      fontSize: "13px",
                      fontWeight: 600,
                      px: 2.5,
                    }}
                  >
                    Apply Override
                  </Button>
                </DialogActions>
              </Dialog>
            );
          })()}
        </>
      )}
    </Box>
  );
};

export const PlanOfCareActiveCreatePage: React.FC = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const organization = useSelector((state: RootState) => state.auth.organization);
  const superOrganization = useSelector((state: RootState) => state.auth.superOrganization);
  const [appointmentTypes, setAppointmentTypes] = React.useState<AppointmentType[]>([]);

  const [activePlanOfCare, setActivePlanOfCare] = React.useState<CompleteActivePlanOfCare | null>(null);
  const [generationMethod, setGenerationMethod] = React.useState<"auto" | "manual">("auto");
  const [showPreview, setShowPreview] = React.useState<boolean>(false);
  const [saving, setSaving] = React.useState<boolean>(false);
  const [savingStep, setSavingStep] = React.useState<"idle" | "saving" | "sending">("idle");
  const [savingWithoutBookingLink, setSavingWithoutBookingLink] = React.useState<boolean>(false);

  const [visitsPerWeek, setVisitsPerWeek] = React.useState(2);
  const [gap, setGap] = React.useState(0);
  const [firstVisit, setFirstVisit] = React.useState<AppointmentType | undefined>(undefined);
  const [mostVisits, setMostVisits] = React.useState<AppointmentType | undefined>(undefined);

  const [appointmentData, setAppointmentData] = React.useState<WebptPatientAppointment[]>([]);
  const [caseStudyId, setCaseStudyId] = React.useState<number>(0);
  const [startDate, setStartDate] = React.useState<string>("");
  const [endDate, setEndDate] = React.useState<string>("");
  const [numberOfWeeks, setNumberOfWeeks] = React.useState<number>(0);
  const [authorization, setAuthorization] = React.useState<WebptPlanOfCareMetadata | null>(null);
  const { insurances } = useSelector((state: RootState) => state.insurances);

  const [caseStudies, setCaseStudies] = React.useState<{ id: number; name: string; isActive: boolean }[]>([]);

  const patientId = useParams().patientId || "";
  const patients = useSelector((state: RootState) => state.patients.patientMap);
  const patient = patients[patientId];

  const authorizationSourcePriority = React.useMemo(() => {
    if (!patient?.patient.insuranceId || !insurances.length) return undefined;
    return insurances.find((i) => i.insuranceId === patient.patient.insuranceId)?.authorizationSourcePriority;
  }, [patient?.patient.insuranceId, insurances]);

  const [creating, setCreating] = React.useState<boolean>(false);
  const [autoGenerating, setAutoGenerating] = React.useState<boolean>(false);

  const [loadingAppointmentTypes, setLoadingAppointmentTypes] = React.useState<boolean>(true);
  const [loadingAppointmentData, setLoadingAppointmentData] = React.useState<boolean>(true);
  const [loadingCaseStudies, setLoadingCaseStudies] = React.useState<boolean>(true);
  const [loadingPatient, setLoadingPatient] = React.useState<boolean>(true);
  const [loadingAuthorization, setLoadingAuthorization] = React.useState<boolean>(true);

  const dispatch = useAppDispatch();
  const { showMessage } = useSnackbar();
  const navigate = useNavigate();

  // Provider & Provider Group state
  const webptProviders = useSelector((state: RootState) => state.webptIntegration.providers);
  const providerGroups = useSelector((state: RootState) => state.providers.providerGroups);
  const [selectedProviders, setSelectedProviders] = React.useState<string[]>([]);
  const [providerGroupId, setProviderGroupId] = React.useState<string | undefined>(undefined);

  const providerOptions = webptProviders.map((provider) => ({
    value: provider.id,
    label: provider.name,
  }));
  const providerGroupOptions = providerGroups.map((group) => ({
    value: group.providerGroupId,
    label: group.name,
  }));

  const handleProviderGroupChange = (newProviderGroupId: string) => {
    setProviderGroupId(newProviderGroupId || undefined);
    if (newProviderGroupId) {
      const group = providerGroups?.find((g) => g.providerGroupId === newProviderGroupId);
      if (group) {
        setSelectedProviders(group.providerIds);
      }
    }
  };

  // Fetch providers and provider groups
  React.useEffect(() => {
    if (user?.token && user?.organizationId) {
      dispatch(fetchWebptProviders({ token: user.token, organizationId: user.organizationId }));
      dispatch(fetchProviderGroups(user.token));
    }
  }, [user?.token, user?.organizationId]);

  React.useEffect(() => {
    if (user?.token && !insurances.length) {
      dispatch(fetchInsurances(user.token));
    }
  }, [dispatch, user]);

  const [bypassAuthorization, setBypassAuthorization] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (!patient) return;
    if (!insurances.length) return;

    const byPassAuthInsuranceIds = new Set(insurances.filter((insurance) => insurance.byPassAuthorization).map((insurance) => insurance.insuranceId));
    if (patient?.patient.insuranceId && byPassAuthInsuranceIds.has(patient.patient.insuranceId)) {
      setBypassAuthorization(true);
    }
  }, [patient, insurances]);

  React.useEffect(() => {
    if (appointmentTypes.length === 0) return;
    const caseStudy = caseStudies.find((study) => study.id === caseStudyId);
    const nonEvalTypes = appointmentTypes.filter((type) => type.category !== AppointmentCategory.evaluation);
    const match = matchAppointmentTypeForCaseStudy(nonEvalTypes, caseStudy?.name, patient?.patient.insuranceId);
    const fallback = appointmentTypes.find((type) => type.name === "Follow-Up");
    setMostVisits(match ?? fallback);

    const evalMatch = matchAppointmentTypeForCaseStudy(appointmentTypes, caseStudy?.name, patient?.patient.insuranceId, AppointmentCategory.evaluation);
    const initialExamination = appointmentTypes.find((type) => type.name === "Initial Examination");
    setFirstVisit(evalMatch ?? initialExamination);
  }, [caseStudyId, caseStudies, appointmentTypes, patient?.patient.insuranceId]);

  const recalculatePlanOfCare = () => {
    console.log("Recalculating plan of care...");
    if (!activePlanOfCare) return;
    if (!appointmentData) return;
    if (appointmentTypes.length === 0) return;
    console.log("Recalculating plan of care...got all data");
    let poc = activePlanOfCare;

    if (bypassAuthorization) {
      poc = { ...poc, ignoreAuthorization: true };
    } else {
      poc = { ...poc, ignoreAuthorization: false };
    }

    if (superOrganization?.autoExtendToWeeks) {
      poc = extendPlanOfCare(poc, appointmentData, appointmentTypes, DateTime.now().plus({ weeks: superOrganization?.numberOfWeeksToAutoExtend }).toISO());
    }

    if (authorization) {
      poc = addAuthorizationToPlanOfCare(poc, authorization);
      if (superOrganization?.autoTruncateAppointments && !poc.preserve) {
        poc = truncatePlanOfCareToAuthorization(poc, authorization);
      }
      if (superOrganization?.autoExtendToAuth) {
        poc = populatePlanOfCare(poc, appointmentData, appointmentTypes);
        poc = extendPlanOfCareToAuthorization(poc, appointmentData, appointmentTypes, authorization);
      }
      if (superOrganization?.autoExtendToPoc && authorization.endDate) {
        poc = extendPlanOfCare(poc, appointmentData, appointmentTypes, authorization.endDate);
      }
    }

    poc = populatePlanOfCare(poc, appointmentData, appointmentTypes);

    setActivePlanOfCare(poc);
  };

  React.useEffect(() => {
    console.log("Active plan of care changed, recalculating plan of care...");
    recalculatePlanOfCare();
  }, [activePlanOfCare?.activePlanOfCareId]);

  React.useEffect(() => {
    console.log("Authorization changed, recalculating plan of care...");
    recalculatePlanOfCare();
  }, [authorization, bypassAuthorization]);

  const autoGeneratePlanOfCare = async () => {
    if (!user?.token) return;
    setAutoGenerating(true);
    try {
      const response = await api.get(
        `/plan-of-care/auto-generated-plan-of-care?webptPatientId=${patient[EHR.webpt]?.webptPatientId}&caseId=${caseStudyId}&patientId=${patientId}&mostVisits=${mostVisits?.appointmentTypeId || ""}&mostEvalVisits=${firstVisit?.appointmentTypeId || ""}`,
        user.token,
      );
      const planOfCare: CompleteActivePlanOfCare = response.data;
      planOfCare.patientId = patientId;
      setActivePlanOfCare(planOfCare);
      setShowPreview(true);
      showMessage("Plan of care generated successfully", "success");
    } catch (error) {
      console.error("Unable to read plan of care from WebPT", error);
      showMessage("Unable to auto-generate plan of care from WebPT", "error");
    } finally {
      setAutoGenerating(false);
    }
  };

  const generatePlanOfCare = () => {
    const pocTemplate = generatePlanOfCareTemplate();
    const caseStudy = caseStudies.find((study) => study.id === caseStudyId);
    if (!caseStudy) return;
    const poc = convertTemplatePlanOfCareToActive(patientId, startDate, pocTemplate, appointmentData, appointmentTypes, caseStudy);
    poc.defaultAppointmentTypeId = mostVisits?.appointmentTypeId || "";
    setActivePlanOfCare(poc);
    setShowPreview(true);
  };

  const savePlanOfCare = async () => {
    if (!user?.token || !activePlanOfCare) return;
    setSaving(true);
    setSavingStep("saving");

    try {
      // Step 1: Save the plan
      await api.post("/plan-of-care/active", user.token, {
        ...activePlanOfCare,
        webptProviderIds: selectedProviders,
        providerGroupId: providerGroupId || null,
      });
      // Step 2: Send booking link to patient
      setSavingStep("sending");
      try {
        await api.post(`/bookings/plan-of-care/send`, user.token, {
          patientId: patientId,
          organizationId: user.organizationId,
          type: "new",
        });
        showMessage("Plan saved and booking link sent to patient", "success");
      } catch (linkError) {
        console.error("Error sending booking link:", linkError);
        showMessage("Unable to send booking link to patient", "error");
      }

      // Navigate after both operations complete
      setSavingStep("idle");
      navigate(`/dashboard/patients/${patientId}`);
    } catch (error) {
      console.error("Error saving plan of care:", error);
      showMessage("Error saving plan of care", "error");
      setSavingStep("idle");
    } finally {
      setSaving(false);
    }
  };

  const savePlanOfCareWithoutBookingLink = async () => {
    if (!user?.token || !activePlanOfCare) return;
    setSavingWithoutBookingLink(true);

    try {
      // Step 1: Save the plan
      await api.post("/plan-of-care/active", user.token, {
        ...activePlanOfCare,
        webptProviderIds: selectedProviders,
        providerGroupId: providerGroupId || null,
      });
      // Navigate after both operations complete
      navigate(`/dashboard/patients/${patientId}`);
    } catch (error) {
      console.error("Error saving plan of care:", error);
      showMessage("Error saving plan of care", "error");
    } finally {
      setSavingWithoutBookingLink(false);
    }
  };

  const generatePlanOfCareTemplate = () => {
    const superOrganizationId = user?.superOrganizationId || "";
    const organizationId = user?.organizationId || "";

    const planOfCareId = uuidv4();
    // Use numberOfWeeks state if set, otherwise calculate from endDate
    const weeksCount = numberOfWeeks > 0 ? numberOfWeeks : Math.round(DateTime.fromISO(endDate).endOf("week").diff(DateTime.fromISO(startDate).startOf("week"), "weeks").weeks);

    let isFirstVisit = true;

    const caseStudy = caseStudies.find((study) => study.id === caseStudyId);
    const caseStudyName = caseStudy?.name || "Plan of Care";

    const template: CompleteTemplatePlanOfCare = {
      superOrganizationId: user?.superOrganizationId || "",
      organizationId: user?.organizationId || "",

      templatePlanOfCareId: planOfCareId,
      name: caseStudyName,
      numAppointments: weeksCount * visitsPerWeek,
      gap: gap,

      templatePlanOfCareWeeks: [],
      templatePlanOfCareWeekIds: [],
    };

    for (let i = 0; i < weeksCount; i++) {
      const weekId = uuidv4();
      const week: CompleteTemplatePlanOfCareWeek = {
        superOrganizationId: superOrganizationId,
        organizationId: organizationId,

        templatePlanOfCareId: planOfCareId,
        templatePlanOfCareWeekId: weekId,

        templatePlanOfCareAppointments: [],
        templatePlanOfCareAppointmentIds: [],
      };

      for (let j = 0; j < visitsPerWeek; j++) {
        let appointmentTypeId = mostVisits?.appointmentTypeId || "";
        if (isFirstVisit) {
          appointmentTypeId = firstVisit?.appointmentTypeId || "";
          isFirstVisit = false;
        }

        const appointmentId = uuidv4();
        const appointment: TemplatePlanOfCareAppointment = {
          superOrganizationId: superOrganizationId,
          organizationId: organizationId,

          templatePlanOfCareId: planOfCareId,
          templatePlanOfCareWeekId: weekId,
          templatePlanOfCareAppointmentId: appointmentId,

          appointmentTypeId: appointmentTypeId,
        };
        week.templatePlanOfCareAppointments.push(appointment);
        week.templatePlanOfCareAppointmentIds.push(appointmentId);
      }

      template.templatePlanOfCareWeeks.push(week);
    }

    return template;
  };

  const handlePrependPlanOfCareWeek = () => {
    setActivePlanOfCare((prev) => {
      if (!prev) return prev;
      const updated = prependPlanOfCareWeek(prev, appointmentData, appointmentTypes);
      // Update startDate to the new first week's start date
      if (updated.activePlanOfCarePeriods.length > 0) {
        const firstWeek = updated.activePlanOfCarePeriods[0];
        setStartDate(firstWeek.periodStart);
      }
      return updated;
    });
  };

  const handleCreatePlanOfCareWeek = () => {
    setActivePlanOfCare((prev) => {
      if (!prev) return prev;
      const updated = createPlanOfCareWeek(prev, appointmentData, appointmentTypes);
      // Update endDate to the new last week's end date
      if (updated.activePlanOfCarePeriods.length > 0) {
        const lastWeek = updated.activePlanOfCarePeriods[updated.activePlanOfCarePeriods.length - 1];
        setEndDate(lastWeek.periodEnd);
      }
      return updated;
    });
  };

  const handleRemoveLastPlanOfCareWeek = () => {
    setActivePlanOfCare((prev) => {
      if (!prev || prev.activePlanOfCarePeriods.length <= 1) return prev;

      const lastWeek = prev.activePlanOfCarePeriods[prev.activePlanOfCarePeriods.length - 1];
      const lastWeekId = lastWeek.activePlanOfCarePeriodId;

      // Check if the last week's end date matches the current endDate
      // Only update endDate if it matches (meaning it wasn't auto-extended by authorization)
      const shouldUpdateEndDate = lastWeek.periodEnd === endDate;

      const updated = deletePlanOfCareWeek(prev, lastWeekId, appointmentData, appointmentTypes);

      if (shouldUpdateEndDate && updated.activePlanOfCarePeriods.length > 0) {
        const newLastWeek = updated.activePlanOfCarePeriods[updated.activePlanOfCarePeriods.length - 1];
        setEndDate(newLastWeek.periodEnd);
      }

      return updated;
    });
  };

  const handleUpdatePlanOfCareWeek = (week: CompleteActivePlanOfCarePeriod) => {
    const truncated = authorization && superOrganization?.autoTruncateAppointments && !activePlanOfCare?.preserve ? truncatePeriodToAuthorization(week, authorization) : week;
    setActivePlanOfCare((prev) => prev && updatePlanOfCareWeek(prev, truncated, appointmentData, appointmentTypes));
  };

  const handleDeletePlanOfCareWeek = (weekId: string) => {
    setActivePlanOfCare((prev) => prev && deletePlanOfCareWeek(prev, weekId, appointmentData, appointmentTypes));
  };

  const updateAppointmentData = (data: WebptPatientAppointment[]) => {
    setAppointmentData(data);
    setStartDate(data[0]?.startString || "");
  };

  async function fetchCaseStudies() {
    setLoadingCaseStudies(true);
    if (!user?.token || !patient?.[EHR.webpt]?.webptPatientId) {
      setLoadingCaseStudies(false);
      return;
    }
    try {
      const response = await api.get(`/webpt/patients/${patient[EHR.webpt]?.webptPatientId}`, user.token);
      const data = response.data;
      const studies = data.caseStudies;
      setCaseStudies(studies);
    } catch (error) {
      console.error("Error fetching case studies:", error);
    } finally {
      setLoadingCaseStudies(false);
    }
  }

  const fetchPatientAuthorization = async (caseStudyId: number) => {
    setLoadingAuthorization(true);
    if (!user?.token) {
      setLoadingAuthorization(false);
      return;
    }
    try {
      const response = await api.get(`/webpt/patients/${patientId}/authorization?caseStudyId=${caseStudyId}`, user.token);
      const data = response.data as WebptPlanOfCareMetadata;
      setAuthorization(data);
    } catch (error) {
      console.error("Error fetching patient authorization:", error);
    } finally {
      setLoadingAuthorization(false);
    }
  };

  const fetchAppointmentTypes = async () => {
    setLoadingAppointmentTypes(true);
    if (!user?.token) {
      setLoadingAppointmentTypes(false);
      return;
    }
    try {
      const response = await api.get(`/appointment-types`, user.token);
      const sortedAppointmentTypes = response.data.sort((a: AppointmentType, b: AppointmentType) => a.name.localeCompare(b.name)) as AppointmentType[];
      setAppointmentTypes(sortedAppointmentTypes);

      const followUp = sortedAppointmentTypes.find((type) => type.name === "Follow-Up");
      const initialExamination = sortedAppointmentTypes.find((type) => type.name === "Initial Examination");

      setMostVisits(followUp);
      setFirstVisit(initialExamination);
    } catch (error) {
      console.error("Error fetching appointment types:", error);
      showMessage("Error fetching appointment types.", "error");
      setAppointmentTypes([]);
    } finally {
      setLoadingAppointmentTypes(false);
    }
  };

  const fetchAppointmentData = async () => {
    setLoadingAppointmentData(true);
    if (!user?.token) {
      setLoadingAppointmentData(false);
      return;
    }
    try {
      const response = await api.get(`/webpt/patients/${patientId}/appointments`, user.token);
      updateAppointmentData(response.data);
      const lastAppointment = response.data[response.data.length - 1];
      const caseStudyId = lastAppointment?.CaseID || 0;
      setCaseStudyId(caseStudyId);
      const caseAppointments = response.data.filter((appt: WebptPatientAppointment) => appt.CaseID === caseStudyId && appt.apptStatus !== "Canc" && appt.apptStatus !== "NoSh");
      setStartDate(caseAppointments[0]?.startString);
    } catch (error) {
      console.error("Error fetching appointment data:", error);
      showMessage("Error fetching appointment data.", "error");
      setAppointmentData([]);
    } finally {
      setLoadingAppointmentData(false);
    }
  };

  const fetchPatient = async () => {
    setLoadingPatient(true);
    if (!user?.token) {
      setLoadingPatient(false);
      return;
    }
    try {
      await dispatch(getWaitlistPatient({ patientId, token: user.token }));
    } catch (error) {
      console.error("Error fetching patient:", error);
      showMessage("Error fetching patient.", "error");
    } finally {
      setLoadingPatient(false);
    }
  };

  React.useEffect(() => {
    if (user?.token) {
      fetchAppointmentTypes();
      fetchAppointmentData();
      fetchPatient();
    }
  }, [user?.token]);

  React.useEffect(() => {
    fetchCaseStudies();
  }, [user?.token, patientId, patient?.[EHR.webpt]?.webptPatientId]);

  React.useEffect(() => {
    if (caseStudyId) {
      fetchPatientAuthorization(caseStudyId);
    }
  }, [user?.token, caseStudyId]);

  const extraAppointments = activePlanOfCare?.extraAppointments || [];

  const isLoading = loadingAppointmentTypes || loadingAppointmentData || loadingCaseStudies || loadingPatient;

  return (
    <Box
      sx={{
        marginTop: { xs: "75px", sm: 0 },
        color: "#FFF",
      }}
    >
      <SignoutHeader />

      {/* Page Header */}
      <Box sx={{ mb: 3 }}>
        <Link to={`/dashboard/patients/${patientId}`} style={{ textDecoration: "none" }}>
          <SecondaryButton
            sx={{
              display: { xs: "none", lg: "flex" },
              alignItems: "center",
              marginRight: "16px",
            }}
          >
            <ArrowBackIcon sx={{ marginRight: "10px" }} /> Back
          </SecondaryButton>
        </Link>
        <Typography variant="h4" fontWeight="700" sx={{ mb: 0.5, mt: 2 }}>
          Create Plan of Care
        </Typography>
        <Typography variant="body1" sx={{ color: Colors.info }}>
          {patient ? `${patient.patient.firstName} ${patient.patient.lastName}` : "Loading patient..."}
        </Typography>
      </Box>

      {/* Loading States */}
      {isLoading && (
        <Box sx={{ mt: 4, display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
          <CircularProgress size={28} />
          <Typography sx={{ fontSize: "13px", color: "rgba(255,255,255,0.5)" }}>
            {"Loading " + [
              loadingPatient && "patient",
              loadingAppointmentTypes && "appointment types",
              loadingAppointmentData && "visit history",
              loadingCaseStudies && "case studies",
              loadingAuthorization && "authorization",
            ].filter(Boolean).join(", ") + "..."}
          </Typography>
        </Box>
      )}

      {!isLoading && (
        <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start" }}>
          {/* Left Panel: Configuration Form */}
          <Box
            sx={{
              width: showPreview ? "380px" : "100%",
              maxWidth: showPreview ? "380px" : "800px",
              flexShrink: 0,
              transition: "width 0.3s ease",
            }}
          >
            {/* Case Study Selection - Compact */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="600" sx={{ mb: 1, fontSize: "13px" }}>
                Case Study
              </Typography>
              <Select
                value={caseStudyId || 0}
                onChange={(e) => {
                  const value = e.target.value as number;
                  setStartDate(appointmentData.filter((appt) => appt.CaseID === value && appt.apptStatus !== "Canc" && appt.apptStatus !== "NoSh")[0]?.startString || "");
                  setCaseStudyId(value);
                  setActivePlanOfCare(null);
                  setAuthorization(null);
                  setShowPreview(false);
                }}
                displayEmpty
                fullWidth
                size="small"
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: Colors.border },
                }}
              >
                <MenuItem value={0} disabled>
                  Select Case Study
                </MenuItem>
                {caseStudies.map((study) => (
                  <MenuItem key={study.id} value={study.id}>
                    {study.isActive ? <ListItemText primary={study.name} /> : <ListItemText primary={`${study.name} (inactive)`} primaryTypographyProps={{ color: Colors.error }} />}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            {/* Configuration Card */}
            {caseStudyId > 0 && (
              <Box
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.04)",
                  borderRadius: "12px",
                  padding: "20px",
                  border: `1px solid ${Colors.border}`,
                }}
              >
                <Typography variant="h6" fontWeight="600" sx={{ mb: 2, fontSize: "16px" }}>
                  Configure Plan
                </Typography>

                {/* Method Tabs - Compact */}
                <Box
                  sx={{
                    display: "flex",
                    backgroundColor: "rgba(255, 255, 255, 0.05)",
                    borderRadius: "8px",
                    p: 0.5,
                    mb: 4,
                  }}
                >
                  <Box
                    onClick={() => setGenerationMethod("auto")}
                    sx={{
                      flex: 1,
                      textAlign: "center",
                      py: 1,
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: 600,
                      backgroundColor: generationMethod === "auto" ? Colors.primary : "transparent",
                      color: generationMethod === "auto" ? "#FFF" : "rgba(255, 255, 255, 0.5)",
                      transition: "all 0.2s ease",
                    }}
                  >
                    Auto-Generate
                  </Box>
                  <Box
                    onClick={() => setGenerationMethod("manual")}
                    sx={{
                      flex: 1,
                      textAlign: "center",
                      py: 1,
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: 600,
                      backgroundColor: generationMethod === "manual" ? Colors.primary : "transparent",
                      color: generationMethod === "manual" ? "#FFF" : "rgba(255, 255, 255, 0.5)",
                      transition: "all 0.2s ease",
                    }}
                  >
                    Manual Entry
                  </Box>
                </Box>

                {/* Auto-Generate Form - Compact */}
                {generationMethod === "auto" && (
                  <Box>
                    {/* Description */}
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mb: 3,
                        color: "rgba(255, 255, 255, 0.5)",
                        lineHeight: 1.6,
                        fontStyle: "italic",
                        fontSize: "12px",
                      }}
                    >
                      Automatically creates the plan of care from WebPT based on provider documentation and patient visit history.
                    </Typography>

                    {/* Default Appointment Type */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body2" fontWeight="600" sx={{ mb: 1, fontSize: "14px", color: "#FFF" }}>
                        Default Appointment Type
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          mb: 1.5,
                          color: "rgba(255, 255, 255, 0.5)",
                          lineHeight: 1.5,
                          fontSize: "12px",
                        }}
                      >
                        Appointment type for regular follow-up visits
                      </Typography>
                      <Select
                        value={mostVisits?.appointmentTypeId || ""}
                        onChange={(e) => {
                          const selectedType = appointmentTypes.find((type) => type.appointmentTypeId === e.target.value);
                          setMostVisits(selectedType || undefined);
                        }}
                        fullWidth
                        size="small"
                        sx={{
                          backgroundColor: "rgba(255, 255, 255, 0.05)",
                          borderRadius: "6px",
                          "& .MuiOutlinedInput-notchedOutline": { borderColor: Colors.border },
                          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: Colors.border },
                        }}
                      >
                        {filterAppointmentTypesForPicker(appointmentTypes, mostVisits?.appointmentTypeId, organization?.organizationId).map((type) => (
                          <MenuItem key={type.appointmentTypeId} value={type.appointmentTypeId}>
                            {type.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>

                    {/* Provider Group */}
                    {providerGroupOptions.length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="body2" fontWeight="600" sx={{ mb: 0.5, fontSize: "14px", color: "#FFF" }}>
                          Provider Group
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "rgba(255, 255, 255, 0.5)",
                            fontSize: "12px",
                            lineHeight: 1.4,
                            display: "block",
                            mb: 1,
                          }}
                        >
                          Assign a provider group to restrict scheduling to those providers.
                        </Typography>
                        <Select
                          value={providerGroupId || ""}
                          onChange={(e) => handleProviderGroupChange(e.target.value)}
                          displayEmpty
                          fullWidth
                          size="small"
                          sx={{
                            backgroundColor: "rgba(255, 255, 255, 0.05)",
                            borderRadius: "8px",
                            color: "#FFF",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: Colors.border,
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: Colors.border,
                            },
                          }}
                        >
                          <MenuItem value="">
                            <em style={{ color: "rgba(255, 255, 255, 0.5)" }}>None</em>
                          </MenuItem>
                          {providerGroupOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </Box>
                    )}

                    {/* Providers */}
                    {providerOptions.length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                          <Typography variant="body2" fontWeight="600" sx={{ fontSize: "14px", color: "#FFF" }}>
                            Providers
                          </Typography>
                          {selectedProviders.length === providerOptions.length ? (
                            <Button
                              onClick={() => setSelectedProviders([])}
                              sx={{
                                textTransform: "none",
                                fontSize: "11px",
                                color: "rgba(255, 255, 255, 0.5)",
                                minWidth: "auto",
                                p: 0,
                                "&:hover": { color: "#FFF" },
                              }}
                            >
                              Deselect All
                            </Button>
                          ) : (
                            <Button
                              onClick={() => setSelectedProviders(providerOptions.map((o) => o.value))}
                              sx={{
                                textTransform: "none",
                                fontSize: "11px",
                                color: "rgba(255, 255, 255, 0.5)",
                                minWidth: "auto",
                                p: 0,
                                "&:hover": { color: "#FFF" },
                              }}
                            >
                              Select All
                            </Button>
                          )}
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "rgba(255, 255, 255, 0.5)",
                            fontSize: "12px",
                            lineHeight: 1.4,
                            display: "block",
                            mb: 1,
                          }}
                        >
                          Select individual providers for this plan of care.
                        </Typography>
                        <Select
                          disabled={!!providerGroupId}
                          multiple
                          displayEmpty
                          value={selectedProviders}
                          onChange={(e) => setSelectedProviders(e.target.value as string[])}
                          renderValue={(selected) => {
                            if (selected.length === 0) {
                              return <Typography sx={{ color: "rgba(255, 255, 255, 0.4)", fontSize: "13px" }}>Select providers</Typography>;
                            }
                            return (selected as string[])
                              .map((value) => providerOptions.find((option) => option.value === value)?.label)
                              .filter(Boolean)
                              .join(", ");
                          }}
                          fullWidth
                          size="small"
                          sx={{
                            backgroundColor: "rgba(255, 255, 255, 0.05)",
                            borderRadius: "8px",
                            color: "#FFF",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: Colors.border,
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: Colors.border,
                            },
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                maxHeight: 250,
                              },
                            },
                          }}
                        >
                          {providerOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              <Checkbox checked={selectedProviders.indexOf(option.value) > -1} size="small" />
                              <ListItemText primary={option.label} />
                            </MenuItem>
                          ))}
                        </Select>
                        {providerGroupId && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: "rgba(255, 255, 255, 0.4)",
                              fontSize: "11px",
                              mt: 0.75,
                              display: "block",
                            }}
                          >
                            Providers are managed by the selected provider group.
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* Ignore Authorization */}
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
                        <Switch checked={bypassAuthorization} onChange={(event) => setBypassAuthorization(event.target.checked)} size="small" />
                        <Typography variant="body2" fontWeight="600" sx={{ fontSize: "14px", color: "#FFF" }}>
                          Ignore Visit Restrictions
                        </Typography>
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          color: "rgba(255, 255, 255, 0.5)",
                          lineHeight: 1.5,
                          fontSize: "12px",
                          ml: 5.5,
                        }}
                      >
                        Allow scheduling visits beyond insurance authorized visit limits.
                      </Typography>
                    </Box>

                    {/* Generate Button */}
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={autoGeneratePlanOfCare}
                      disabled={!caseStudyId || autoGenerating}
                      sx={{
                        textTransform: "none",
                        py: 1.25,
                        fontSize: "14px",
                        fontWeight: 600,
                      }}
                    >
                      {autoGenerating ? "Generating..." : showPreview ? "Regenerate Plan" : "Generate Plan"}
                    </Button>
                  </Box>
                )}

                {/* Manual Entry Form - Compact */}
                {generationMethod === "manual" && (
                  <Box>
                    {/* Date Pickers */}
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2.5, alignItems: "center" }}>
                      <DatePicker
                        label="Start Date"
                        value={DateTime.fromISO(startDate)}
                        onChange={(date) => setStartDate(date?.toISO() || "")}
                        slotProps={{
                          textField: {
                            size: "small",
                            sx: {
                              backgroundColor: "rgba(255, 255, 255, 0.05)",
                              borderRadius: "6px",
                              minWidth: "160px",
                            },
                          },
                        }}
                      />
                      <DatePicker
                        label="End Date"
                        value={endDate ? DateTime.fromISO(endDate) : null}
                        onChange={(date) => {
                          setEndDate(date?.toISO() || "");
                          if (date) {
                            setNumberOfWeeks(0); // Clear number of weeks when end date is set
                          }
                        }}
                        slotProps={{
                          textField: {
                            size: "small",
                            sx: {
                              backgroundColor: "rgba(255, 255, 255, 0.05)",
                              borderRadius: "6px",
                              minWidth: "160px",
                            },
                          },
                        }}
                      />
                      <Box sx={{ fontSize: "13px", fontWeight: 600, color: Colors.info }}>OR</Box>
                      <NumberTextField
                        label="Number of Weeks"
                        value={numberOfWeeks}
                        onChange={(value) => {
                          setNumberOfWeeks(value);
                          if (value > 0) {
                            setEndDate(""); // Clear end date when number of weeks is set
                          }
                        }}
                        sx={{
                          minWidth: "140px",
                        }}
                      />
                    </Box>

                    {/* Frequency */}
                    <Box sx={{ mb: 2.5 }}>
                      <Typography variant="body2" fontWeight="600" sx={{ mb: 1, fontSize: "13px", color: "rgba(255, 255, 255, 0.9)" }}>
                        Visits Per Week
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 0.5,
                        }}
                      >
                        {[1, 2, 3, 4, 5].map((num) => (
                          <Box
                            key={num}
                            onClick={() => setVisitsPerWeek(num)}
                            sx={{
                              minWidth: "36px",
                              textAlign: "center",
                              py: 0.5,
                              px: 1.25,
                              borderRadius: "6px",
                              cursor: "pointer",
                              fontSize: "13px",
                              fontWeight: 600,
                              backgroundColor: visitsPerWeek === num ? Colors.primary : "rgba(255, 255, 255, 0.05)",
                              color: visitsPerWeek === num ? "#FFF" : "rgba(255, 255, 255, 0.5)",
                              border: "1px solid " + (visitsPerWeek === num ? Colors.primary : Colors.border),
                              transition: "all 0.15s ease",
                              "&:hover": {
                                backgroundColor: visitsPerWeek === num ? Colors.primary : "rgba(255, 255, 255, 0.08)",
                                borderColor: visitsPerWeek === num ? Colors.primary : Colors.border,
                              },
                            }}
                          >
                            {num}
                          </Box>
                        ))}
                      </Box>
                    </Box>

                    {/* Gap Days */}
                    <Box sx={{ mb: 2.5 }}>
                      <Typography variant="body2" fontWeight="600" sx={{ mb: 1, fontSize: "13px", color: "rgba(255, 255, 255, 0.9)" }}>
                        Gap Between Appointments (Days)
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 0.5,
                        }}
                      >
                        {[0, 1, 2, 3].map((num) => (
                          <Box
                            key={num}
                            onClick={() => setGap(num)}
                            sx={{
                              minWidth: "36px",
                              textAlign: "center",
                              py: 0.5,
                              px: 1.25,
                              borderRadius: "6px",
                              cursor: "pointer",
                              fontSize: "13px",
                              fontWeight: 600,
                              backgroundColor: gap === num ? Colors.primary : "rgba(255, 255, 255, 0.05)",
                              color: gap === num ? "#FFF" : "rgba(255, 255, 255, 0.5)",
                              border: "1px solid " + (gap === num ? Colors.primary : Colors.border),
                              transition: "all 0.15s ease",
                              "&:hover": {
                                backgroundColor: gap === num ? Colors.primary : "rgba(255, 255, 255, 0.08)",
                                borderColor: gap === num ? Colors.primary : Colors.border,
                              },
                            }}
                          >
                            {num}
                          </Box>
                        ))}
                      </Box>
                    </Box>

                    {/* First Visit */}
                    <Box sx={{ mb: 2.5 }}>
                      <Typography variant="body2" fontWeight="600" sx={{ mb: 1, fontSize: "13px", color: "rgba(255, 255, 255, 0.9)" }}>
                        First Visit
                      </Typography>
                      <Select
                        value={firstVisit?.appointmentTypeId || ""}
                        onChange={(e) => {
                          const selectedType = appointmentTypes.find((type) => type.appointmentTypeId === e.target.value);
                          setFirstVisit(selectedType || undefined);
                        }}
                        displayEmpty
                        fullWidth
                        size="small"
                        sx={{
                          backgroundColor: "rgba(255, 255, 255, 0.05)",
                          borderRadius: "6px",
                          fontSize: "13px",
                        }}
                      >
                        <MenuItem value="" disabled>
                          <em>Select appointment type</em>
                        </MenuItem>
                        {filterAppointmentTypesForPicker(appointmentTypes, firstVisit?.appointmentTypeId, organization?.organizationId).map((type) => (
                          <MenuItem key={type.appointmentTypeId} value={type.appointmentTypeId}>
                            {type.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>

                    {/* Follow-Up Visits */}
                    <Box sx={{ mb: 2.5 }}>
                      <Typography variant="body2" fontWeight="600" sx={{ mb: 1, fontSize: "13px", color: "rgba(255, 255, 255, 0.9)" }}>
                        Follow-Up Visits
                      </Typography>
                      <Select
                        value={mostVisits?.appointmentTypeId || ""}
                        onChange={(e) => {
                          const selectedType = appointmentTypes.find((type) => type.appointmentTypeId === e.target.value);
                          setMostVisits(selectedType || undefined);
                        }}
                        displayEmpty
                        fullWidth
                        size="small"
                        sx={{
                          backgroundColor: "rgba(255, 255, 255, 0.05)",
                          borderRadius: "6px",
                          fontSize: "13px",
                        }}
                      >
                        <MenuItem value="" disabled>
                          <em>Select appointment type</em>
                        </MenuItem>
                        {filterAppointmentTypesForPicker(appointmentTypes, mostVisits?.appointmentTypeId, organization?.organizationId).map((type) => (
                          <MenuItem key={type.appointmentTypeId} value={type.appointmentTypeId}>
                            {type.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>

                    {/* Provider Group */}
                    {providerGroupOptions.length > 0 && (
                      <Box sx={{ mb: 2.5 }}>
                        <Typography variant="body2" fontWeight="600" sx={{ mb: 0.5, fontSize: "13px", color: "rgba(255, 255, 255, 0.9)" }}>
                          Provider Group
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "rgba(255, 255, 255, 0.5)",
                            fontSize: "11px",
                            lineHeight: 1.4,
                            display: "block",
                            mb: 1,
                          }}
                        >
                          Assign a provider group to restrict scheduling to those providers.
                        </Typography>
                        <Select
                          value={providerGroupId || ""}
                          onChange={(e) => handleProviderGroupChange(e.target.value)}
                          displayEmpty
                          fullWidth
                          size="small"
                          sx={{
                            backgroundColor: "rgba(255, 255, 255, 0.05)",
                            borderRadius: "6px",
                            color: "#FFF",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: Colors.border,
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: Colors.border,
                            },
                          }}
                        >
                          <MenuItem value="">
                            <em style={{ color: "rgba(255, 255, 255, 0.5)" }}>None</em>
                          </MenuItem>
                          {providerGroupOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </Box>
                    )}

                    {/* Providers */}
                    {providerOptions.length > 0 && (
                      <Box sx={{ mb: 2.5 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                          <Typography variant="body2" fontWeight="600" sx={{ fontSize: "13px", color: "rgba(255, 255, 255, 0.9)" }}>
                            Providers
                          </Typography>
                          {selectedProviders.length === providerOptions.length ? (
                            <Button
                              onClick={() => setSelectedProviders([])}
                              sx={{
                                textTransform: "none",
                                fontSize: "11px",
                                color: "rgba(255, 255, 255, 0.5)",
                                minWidth: "auto",
                                p: 0,
                                "&:hover": { color: "#FFF" },
                              }}
                            >
                              Deselect All
                            </Button>
                          ) : (
                            <Button
                              onClick={() => setSelectedProviders(providerOptions.map((o) => o.value))}
                              sx={{
                                textTransform: "none",
                                fontSize: "11px",
                                color: "rgba(255, 255, 255, 0.5)",
                                minWidth: "auto",
                                p: 0,
                                "&:hover": { color: "#FFF" },
                              }}
                            >
                              Select All
                            </Button>
                          )}
                        </Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "rgba(255, 255, 255, 0.5)",
                            fontSize: "11px",
                            lineHeight: 1.4,
                            display: "block",
                            mb: 1,
                          }}
                        >
                          Select individual providers for this plan of care.
                        </Typography>
                        <Select
                          disabled={!!providerGroupId}
                          multiple
                          displayEmpty
                          value={selectedProviders}
                          onChange={(e) => setSelectedProviders(e.target.value as string[])}
                          renderValue={(selected) => {
                            if (selected.length === 0) {
                              return <Typography sx={{ color: "rgba(255, 255, 255, 0.4)", fontSize: "13px" }}>Select providers</Typography>;
                            }
                            return (selected as string[])
                              .map((value) => providerOptions.find((option) => option.value === value)?.label)
                              .filter(Boolean)
                              .join(", ");
                          }}
                          fullWidth
                          size="small"
                          sx={{
                            backgroundColor: "rgba(255, 255, 255, 0.05)",
                            borderRadius: "6px",
                            color: "#FFF",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: Colors.border,
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: Colors.border,
                            },
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                maxHeight: 250,
                              },
                            },
                          }}
                        >
                          {providerOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              <Checkbox checked={selectedProviders.indexOf(option.value) > -1} size="small" />
                              <ListItemText primary={option.label} />
                            </MenuItem>
                          ))}
                        </Select>
                        {providerGroupId && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: "rgba(255, 255, 255, 0.4)",
                              fontSize: "11px",
                              mt: 0.75,
                              display: "block",
                            }}
                          >
                            Providers are managed by the selected provider group.
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* Ignore Authorization */}
                    <Box sx={{ mb: 2.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
                        <Switch checked={bypassAuthorization} onChange={(event) => setBypassAuthorization(event.target.checked)} size="small" />
                        <Typography variant="body2" fontWeight="600" sx={{ fontSize: "13px", color: "rgba(255, 255, 255, 0.9)" }}>
                          Ignore Visit Restrictions
                        </Typography>
                      </Box>
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          color: "rgba(255, 255, 255, 0.5)",
                          lineHeight: 1.5,
                          fontSize: "11px",
                          ml: 5.5,
                        }}
                      >
                        Allow scheduling visits beyond insurance authorized visit limits.
                      </Typography>
                    </Box>

                    {/* Generate Button */}
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={generatePlanOfCare}
                      disabled={!caseStudyId || !startDate || (!endDate && !(numberOfWeeks > 0))}
                      sx={{
                        textTransform: "none",
                        py: 1,
                        fontSize: "13px",
                        fontWeight: 600,
                        mt: 1,
                      }}
                    >
                      {showPreview ? "Regenerate" : "Generate Preview"}
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </Box>

          {/* Right Panel: Preview */}
          {showPreview && activePlanOfCare && (
            <Box sx={{ flex: 1, minWidth: 0, maxWidth: "100%" }}>
              {/* Preview Header */}
              <Box
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.04)",
                  border: `1px solid ${Colors.border}`,
                  borderRadius: "10px",
                  padding: "16px 20px",
                  mb: 3,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography variant="body2" fontWeight="600" sx={{ color: "rgba(255, 255, 255, 0.9)", fontSize: "14px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      Preview
                    </Typography>
                    <Box sx={{ height: "16px", width: "1px", backgroundColor: "rgba(255, 255, 255, 0.1)" }} />
                    <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.5)", fontSize: "13px" }}>
                      {activePlanOfCare.totalAppointments || 0} visits · {activePlanOfCare.activePlanOfCarePeriods.length} weeks
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                    <Tooltip title={activePlanOfCare.preserve ? "Remove protection from auto-adjustments" : "Protect entire plan from auto-adjustments"} arrow>
                      <IconButton
                        size="small"
                        onClick={() => setActivePlanOfCare((prev) => prev && { ...prev, preserve: !prev.preserve })}
                        sx={{
                          color: activePlanOfCare.preserve ? Colors.primary : "rgba(255,255,255,0.3)",
                          "&:hover": { color: Colors.primary },
                        }}
                      >
                        {activePlanOfCare.preserve ? <LockIcon sx={{ fontSize: "18px" }} /> : <LockOpenIcon sx={{ fontSize: "18px" }} />}
                      </IconButton>
                    </Tooltip>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setShowPreview(false);
                        setActivePlanOfCare(null);
                      }}
                      sx={{
                        textTransform: "none",
                        fontSize: "13px",
                        borderColor: "rgba(255, 255, 255, 0.15)",
                        color: "rgba(255, 255, 255, 0.7)",
                        fontWeight: 500,
                        "&:hover": {
                          borderColor: "rgba(255, 255, 255, 0.3)",
                          backgroundColor: "rgba(255, 255, 255, 0.05)",
                        },
                      }}
                    >
                      Discard
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={savePlanOfCareWithoutBookingLink}
                      disabled={savingWithoutBookingLink}
                      sx={{
                        textTransform: "none",
                        fontSize: "13px",
                        fontWeight: 600,
                        backgroundColor: Colors.primary,
                        "&:hover": {
                          backgroundColor: Colors.primary,
                          opacity: 0.9,
                        },
                      }}
                    >
                      {savingWithoutBookingLink && "Saving plan..."}
                      {!savingWithoutBookingLink && "Save plan"}
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={savePlanOfCare}
                      disabled={saving}
                      sx={{
                        textTransform: "none",
                        fontSize: "13px",
                        fontWeight: 600,
                        backgroundColor: Colors.success,
                        "&:hover": {
                          backgroundColor: Colors.success,
                          opacity: 0.9,
                        },
                      }}
                    >
                      {savingStep === "saving" && "Saving plan..."}
                      {savingStep === "sending" && "Sending link..."}
                      {savingStep === "idle" && "Save plan & send booking link"}
                    </Button>
                  </Box>
                </Box>

                {/* Info about what happens on save */}
                <Box
                  sx={{
                    mt: 2,
                    pt: 2,
                    borderTop: "1px solid rgba(255, 255, 255, 0.08)",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                      backgroundColor: "rgba(34, 182, 114, 0.15)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      mt: 0.25,
                    }}
                  >
                    <Typography sx={{ fontSize: "11px", fontWeight: 700, color: Colors.success }}>✓</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" fontWeight="600" sx={{ display: "block", color: "rgba(255, 255, 255, 0.9)", fontSize: "12px", mb: 0.5 }}>
                      When you save this plan:
                    </Typography>
                    <Typography variant="caption" sx={{ display: "block", color: "rgba(255, 255, 255, 0.6)", fontSize: "11px", lineHeight: 1.6 }}>
                      The patient will automatically receive a text with a booking link to schedule their visits.
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Weekly Schedule */}
              <Box>
                {/* Weeks */}
                <Box>
                  <PlanOfCareWeeksWithCollapse
                    activePlanOfCare={activePlanOfCare}
                    appointmentTypes={appointmentTypes}
                    handleUpdatePlanOfCareWeek={handleUpdatePlanOfCareWeek}
                    handleDeletePlanOfCareWeek={handleDeletePlanOfCareWeek}
                    defaultAppointmentTypeId={mostVisits?.appointmentTypeId || ""}
                    editable={true}
                    handlePrependPlanOfCareWeek={handlePrependPlanOfCareWeek}
                    handleCreatePlanOfCareWeek={handleCreatePlanOfCareWeek}
                    handleRemoveLastWeek={handleRemoveLastPlanOfCareWeek}
                    authorizationsPerSource={authorization?.authorizationsPerSource}
                    authorizationSourcePriority={authorizationSourcePriority}
                  />

                  {extraAppointments.map((appointment) => {
                    const activePlanOfCareAppointmentId = uuidv4();
                    let appointmentTypeId = appointmentTypes.find((type) => type.webptShortCode === appointment.AptType)?.appointmentTypeId || "";
                    if (appointment.AptType === "CT") appointmentTypeId = "";
                    const pocAppointment: ActivePlanOfCareAppointment = {
                      activePlanOfCareId: activePlanOfCare.activePlanOfCareId,
                      activePlanOfCarePeriodId: "",
                      activePlanOfCareAppointmentId: activePlanOfCareAppointmentId,
                      superOrganizationId: activePlanOfCare.superOrganizationId,
                      organizationId: activePlanOfCare.organizationId,
                      patientId: patientId,

                      appointmentTypeId: appointmentTypeId,
                      appointmentDate: appointment.startString,
                      status: "scheduled",
                    };

                    return (
                      <AppointmentCard
                        key={activePlanOfCareAppointmentId}
                        appointment={pocAppointment}
                        appointmentNumber={0}
                        appointmentTypes={appointmentTypes}
                        updatePlanOfCareAppointment={() => {}}
                        handleDeleteAppointment={() => {}}
                        editable={false}
                        isExtra={true}
                      />
                    );
                  })}
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
